import React, { useState, useEffect, useRef } from 'react';
import * as speechsdk from 'microsoft-cognitiveservices-speech-sdk';
import {
  createAgentManager,
  AgentManager,
  ConnectionState,
  Providers,
  StreamingState,
} from '@d-id/client-sdk';
import ReactPlayer from 'react-player';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import BarChart from '../components/Chat/components/Chart/BarChart';
import PieChart from '../components/Chat/components/Chart/PieChart';
import LineChart from '../components/Chat/components/Chart/LineChart';
import GroupedBarChart from '../components/Chat/components/Chart/GroupedBarChart';
import TemplateMatriculados from '../components/Chat/components/Chart/TemplateMatriculados';
import Dashboard from '../components/Chat/components/Chart/Dashboard';
import UniversityFunnels, { UniversityFunnelsData } from '../components/Chat/components/Chart/UniversityFunnels';
import TitulacionBars, { TitulacionBarsData } from '../components/Chat/components/Chart/TitulacionBars';
import OverviewBars, { OverviewBarsData } from '../components/Chat/components/Chart/OverviewBars';
import RankingTable, { RankingTableData } from '../components/Chat/components/Chart/RankingTable';
import PersonCards, { PersonCardsData } from '../components/Chat/components/Chart/PersonCards';
import DepartmentOrgChart, { DepartmentOrgChartData } from '../components/Chat/components/Chart/DepartmentOrgChart';
import {DashboardData} from '../../models/dashboard';
import TemplateTasas from '../components/Chat/components/Chart/TemplateTasas';
import Chat from '../components/Chat';
import LogoutBtn from '../components/LogoutBtn';
import '../../css/avatar.css';
import useMsalHook from '../../auth/useMsalHook';


//import GPTCEUImg_footer from '../../assets/images/CEU-UCH_footer-blanco.png';

import {
  AvatarConfiguration,
  AvatarExamPayload,
  AvatarResponse,
  isConversationEndedResponse,
  parseAvatarExamPayload,
  sanitizeAvatarExamPayload,
} from '../../models/avatar';
import {
  AvatarInteractionLimitSession,
  ensureMinimumInteractions,
  MIN_INTERVIEW_TOTAL_INTERACTIONS,
  normalizeMaxInteractions,
} from '../../models/avatarInteractionLimit';
import { Message, toMessageFormat } from '../../models/chat';

import micNormalImg from '../../assets/images/microfono.png';
import micListeningImg from '../../assets/images/microfono-escuchando.png';
import micForbiddenImg from '../../assets/images/microfono-prohibido.png';
import { ReactComponent as ThumbUpIcon } from '../../assets/images/thumb-up.svg';
import { ReactComponent as ThumbDownIcon } from '../../assets/images/thumb-down.svg';
import {
  VOLUMEN_ON,
  avatarType,
  usuario,
  getUrlDominio,
} from '../../constants';
import {
  getInitialConfig,
  getGptData,
  getVariableData,
  sendAvatarFeedback,
  sendSatisfactionRating,
  getAzureVoiceToken,
  uploadFiles,
} from '../../services/api';
import { getIdProcesoByTag, getProcesosIdMap } from '../../services/api/procesos';
import { AvatarContext } from '../../context/Avatar/AvatarContext';
import { AvatarContextModel } from '../../context/Avatar/props';
import { ChatContext } from '../../context/Chat/Chat';
import { ChatContextModel } from '../../context/Chat/props';
import { showToast } from '../../helpers/toast';
import NoData from '../components/NoData';
import { AzureTalk } from '../../models/azure';
import { checkMicPermissions } from '../../helpers/mic';
//import { parse } from '@fortawesome/fontawesome-svg-core';
//import Cookies from 'js-cookie';  // Asegúrate de importar js-cookie

var isWaitingResponse: boolean = false;
var isMicOpened: boolean = false; // controla si el micro está abierto/escuchando(true) o cerrado/pausado/inexistente(false)
let speechRef: speechsdk.SpeechRecognizer | null = null;
const isInterviewAvatar = avatarType?.toLowerCase().startsWith('ent_');

const SPEECH_SUBMIT_DELAY_MS = 3000;
const SPEECH_SEGMENTATION_SILENCE_MS = 1000;
// D-ID puede tardar más de 12 s en completar el handshake aunque ya haya
// creado el stream y entregado sus pistas de audio/vídeo.
const DID_CONNECTION_TIMEOUT_MS = 30000;
const DID_SPEAK_START_TIMEOUT_MS = 8000;
const AVATAR_RESPONSE_TIMEOUT_MS = 45000;
const AUDIO_FALLBACK_OFFER_DELAY_MS = 5000;
const VIDEO_STALL_FALLBACK_MS = 4500;
const VIDEO_STALL_CHECK_INTERVAL_MS = 500;
const AUDIO_SPEECH_WATCHDOG_EXTRA_MS = 10000;
const AUDIO_PLAYBACK_GRACE_MS = 1000;

type ConversationPhase = 'idle' | 'listening' | 'thinking' | 'speaking' | 'paused';

const estimateSpeechMs = (text: string): number =>
  Math.min(Math.max(Math.ceil(text.length / 14) * 1000 + 800, 2000), 90000);

type GroupedBarDataset = {
  label: string;
  data: number[];
  backgroundColor: string;
};

type DidTalkStreamResponse = {
  id: string;
  session_id: string;
  offer: RTCSessionDescriptionInit;
  ice_servers?: RTCIceServer[];
};

type ChartDataState = number[] | number[][] | GroupedBarDataset[] | GroupedBarDataset[][];

const buildExamIntroMessage = (examPayload: AvatarExamPayload): string => {
  const examTitle = examPayload.examen.titulo?.trim();
  const totalQuestions = examPayload.examen.preguntas.length;
  const questionLabel = totalQuestions === 1 ? 'pregunta' : 'preguntas';

  if (examTitle) {
    return `${examTitle}. He preparado un examen interactivo con ${totalQuestions} ${questionLabel}. Complétalo y envía tus respuestas cuando termines.`;
  }

  return `He preparado un examen interactivo con ${totalQuestions} ${questionLabel}. Complétalo y envía tus respuestas cuando termines.`;
};

const isTutorPersonalizadoDomain = (dominio?: string): boolean =>
  (dominio ?? '').toLowerCase().includes('tutor personalizado');

const addResourceHint = (rel: string, href: string, as?: string) => {
  if (!href || document.head.querySelector(`link[rel="${rel}"][href="${href}"]`)) {
    return;
  }

  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  if (as) {
    link.as = as;
  }
  document.head.appendChild(link);
};

const getUrlOrigin = (value?: string): string | null => {
  if (!value) {
    return null;
  }

  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
};

const playDidVideo = (video: HTMLVideoElement) => {
  video.play().catch((error) => {
    if (error?.name === 'AbortError') {
      return;
    }

    console.warn('D-ID video play blocked', error);
  });
};

const AvatarApp = () => {
  const { user, userToken, logout } = useMsalHook();

  // useState
  const [displayText, setDisplayText] = useState<string>('Pulsa para conectar');
  const [chatInput, setChatInput] = useState<string>('');
  const [fileDescription, setFileDescription] = useState<string>('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [pendingUploadIdMessage, setPendingUploadIdMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isChatSent, setIsChatSent] = useState<boolean>(false);
  const [hasFirstMessageSent, setHasFirstMessageSent] = useState(false);
  const [isFeedbackSending, setIsFeedbackSending] = useState<boolean>(false);
  const [feedbackRequested, setFeedbackRequested] = useState<boolean>(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);
  const [feedbackEmailInput, setFeedbackEmailInput] = useState<string>('');
  const [isMicEnabled, setIsMicEnabled] = useState<boolean | undefined>(
    undefined
  ); // controla si el micro ha sido creado(true)/pausado(false)/inexistente(undefined)
  const [streamingState, setStreamingState] = useState<string | undefined>(''); // aqui el undefined es solo por tipado, nunca será
  const [micImg, setMicImg] = useState<string>(micNormalImg);
  const [showStreaming, setShowStreaming] = useState<boolean>(true); //deberia ser false, para ver el video de vimeo
  const [isDidStreamVisible, setIsDidStreamVisible] = useState<boolean>(false);
  const [isAudioFallbackMode, setIsAudioFallbackMode] = useState<boolean>(false);
  const [showAudioFallbackOffer, setShowAudioFallbackOffer] = useState<boolean>(false);
  const [isAvatarSpeaking, setIsAvatarSpeaking] = useState<boolean>(false);
  const [isConversationPaused, setIsConversationPaused] = useState<boolean>(false);
  const [isConversationStopped, setIsConversationStopped] = useState<boolean>(false);
  const [micStartFailed, setMicStartFailed] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  const [messages, setMessages] = useState<Message[]>([]);
  const [isBotThinking, setIsBotThinking] = useState<boolean>(false);
  const [initialConfig, setInitialConfig] = useState<
    AvatarConfiguration | undefined
  >(undefined);
  const [interactionLimitReached, setInteractionLimitReached] =
    useState<boolean>(false);
  const [errorOnInitialConfig, setErrorOnInitialConfig] = useState<string>('');

  //chart
  const [chartLabels, setChartLabels] = useState<string[] | string[][]>([]);
  const [chartData, setChartData] = useState<ChartDataState>([]);
  const [showChart, setShowChart] = useState(false);
  // eslint-disable-next-line
  const [numConsultas, setNumConsultas] = useState(0);
  // eslint-disable-next-line
  const [tipoGrafica, settipoGrafica] = useState<string | undefined>(undefined);
  const [chartTitle, setChartTitle] = useState<string | string[]>("");

  //template
  const [isTemplate, setIsTemplate] = useState<string>("");
  const [campusEvolutions, setCampusEvolutions] = useState<{ [campus: string]: { years: string[], values: number[] } }>({});
  const [selectedCampus] = useState<string | null>(null);

  const originalChartLabels = useRef<string[][]>([]);
  const originalChartData = useRef<number[][]>([]);
  const originalChartTitle = useRef<string[] | string>("");
  //const [filteredCampusData, setFilteredCampusData] = useState<{ years: string[], values: number[] } | null>(null);
  const [originalMaxY, setOriginalMaxY] = useState<number | undefined>(undefined);

  const [nationalInternationalData, setNationalInternationalData] = useState<{ years: string[]; nacionales: number[]; internacionales: number[]; } | null>(null);
  const [nuevoIngresoEvolutions, setNuevoIngresoEvolutions] = useState<{ years: string[], values: number[] } | null>(null);
  const [reingresoEvolutions, setReingresoEvolutions] = useState<{ years: string[], values: number[] } | null>(null);
  // eslint-disable-next-line
  const [selectedIngresoReingreso, setSelectedIngresoReingreso] = useState<string | null>(null);
  const [tableData, setTableData] = useState<{ headers: string[], rows: any[] } | null>(null);
  const getChartColors = (count: number): string[] => {
    return Array.from({ length: count }, (_, i) =>
      `hsl(${Math.round((360 * i) / count)}, 70%, 50%)`
    );
  };

  const parseObjectPayload = (payload: unknown): Record<string, any> | null => {
    if (typeof payload === 'string') {
      try {
        const parsed = JSON.parse(payload);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? parsed
          : null;
      } catch {
        return null;
      }
    }

    return payload && typeof payload === 'object' && !Array.isArray(payload)
      ? payload as Record<string, any>
      : null;
  };

  const getChartDataPayload = (response: any): Record<string, any> | null => {
    const directChartData = parseObjectPayload(response?.chart_data);
    if (directChartData) {
      return directChartData;
    }

    const parsedJson = parseObjectPayload(response?.respuesta_json);
    if (!parsedJson) {
      return null;
    }

    return parseObjectPayload(parsedJson.chart_data) || parsedJson;
  };

  const processChartDataResponse = (response: any): boolean => {
    const chartDataPayload = getChartDataPayload(response);
    console.log('[chart_data]', chartDataPayload);
    setPersonCardsData(null);
    setDepartmentOrgChartData(null);

    if (chartDataPayload?.type === 'department-org-chart') {
      const departments = Array.isArray(chartDataPayload.departments)
        ? chartDataPayload.departments
          .filter((department: unknown): department is Record<string, any> =>
            Boolean(department) && typeof department === 'object' && !Array.isArray(department)
          )
          .map((department) => ({
            ...department,
            people: Array.isArray(department.people)
              ? department.people.filter(
                (person: unknown): person is Record<string, unknown> =>
                  Boolean(person) && typeof person === 'object' && !Array.isArray(person)
              )
              : [],
          }))
        : [];

      if (departments.length === 0 || !departments.some((department) => department.people.length > 0)) {
        return false;
      }

      const departmentOrgChartPayload: DepartmentOrgChartData = {
        type: 'department-org-chart',
        title: typeof chartDataPayload.title === 'string'
          ? chartDataPayload.title
          : 'Personas por departamento',
        root: chartDataPayload.root && typeof chartDataPayload.root === 'object'
          ? { label: typeof chartDataPayload.root.label === 'string' ? chartDataPayload.root.label : undefined }
          : undefined,
        departments,
      };

      setDepartmentOrgChartData(departmentOrgChartPayload);
      setUniversityFunnelsData(null);
      setTitulacionBarsData(null);
      setOverviewBarsData(null);
      setRankingTableData(null);
      setChartLabels([]);
      setChartData([]);
      setChartTitle('');
      settipoGrafica(undefined);
      setNumConsultas(1);
      setTableData(null);
      setIsTemplate('');
      setRespuestaJson(null);
      setIsDashboard(false);
      setExamsOnly(null);
      setShowChart(true);
      return true;
    }

    if (chartDataPayload?.type === 'person-cards') {
      const people = Array.isArray(chartDataPayload.people)
        ? chartDataPayload.people.filter(
          (person: unknown): person is Record<string, unknown> =>
            Boolean(person) && typeof person === 'object' && !Array.isArray(person)
        )
        : [];

      if (people.length === 0) {
        return false;
      }

      const personCardsPayload: PersonCardsData = {
        type: 'person-cards',
        title: typeof chartDataPayload.title === 'string'
          ? chartDataPayload.title
          : 'Personas',
        people,
      };

      setPersonCardsData(personCardsPayload);
      setUniversityFunnelsData(null);
      setTitulacionBarsData(null);
      setOverviewBarsData(null);
      setRankingTableData(null);
      setChartLabels([]);
      setChartData([]);
      setChartTitle('');
      settipoGrafica(undefined);
      setNumConsultas(1);
      setTableData(null);
      setIsTemplate('');
      setRespuestaJson(null);
      setIsDashboard(false);
      setExamsOnly(null);
      setShowChart(true);
      return true;
    }

    if (chartDataPayload?.type === 'university-funnels') {
      const funnelsPayload = chartDataPayload as unknown as UniversityFunnelsData;
      if (!Array.isArray(funnelsPayload.funnels) || funnelsPayload.funnels.length === 0) {
        return false;
      }
      setUniversityFunnelsData(funnelsPayload);
      setTitulacionBarsData(null);
      setOverviewBarsData(null);
      setRankingTableData(null);
      setChartLabels([]);
      setChartData([]);
      setChartTitle('');
      settipoGrafica(undefined);
      setNumConsultas(1);
      setTableData(null);
      setIsTemplate('');
      setRespuestaJson(null);
      setIsDashboard(false);
      setExamsOnly(null);
      setShowChart(true);
      return true;
    }

    if (chartDataPayload?.type === 'titulacion-bars') {
      const barsPayload = chartDataPayload as unknown as TitulacionBarsData;
      if (!Array.isArray(barsPayload.bars) || barsPayload.bars.length === 0) {
        return false;
      }
      setTitulacionBarsData(barsPayload);
      setUniversityFunnelsData(null);
      setOverviewBarsData(null);
      setRankingTableData(null);
      setChartLabels([]);
      setChartData([]);
      setChartTitle('');
      settipoGrafica(undefined);
      setNumConsultas(1);
      setTableData(null);
      setIsTemplate('');
      setRespuestaJson(null);
      setIsDashboard(false);
      setExamsOnly(null);
      setShowChart(true);
      return true;
    }

    if (chartDataPayload?.type === 'faculty-bars') {
      const items = Array.isArray(chartDataPayload.items) ? chartDataPayload.items : [];
      if (items.length === 0) {
        return false;
      }
      const bars = items.map((item: any) => ({
        metric: item.label || item.faculty || '',
        label: item.label || item.faculty || '',
        values: item.values || {},
      }));
      const barsPayload: TitulacionBarsData = {
        type: 'faculty-bars',
        title: chartDataPayload.title || '',
        subtitle: chartDataPayload.subtitle,
        courses: Array.isArray(chartDataPayload.courses) ? chartDataPayload.courses : [],
        bars,
        scope: chartDataPayload.scope,
        metric: chartDataPayload.metric,
        metricLabel: chartDataPayload.metricLabel,
      };
      setTitulacionBarsData(barsPayload);
      setUniversityFunnelsData(null);
      setOverviewBarsData(null);
      setRankingTableData(null);
      setChartLabels([]);
      setChartData([]);
      setChartTitle('');
      settipoGrafica(undefined);
      setNumConsultas(1);
      setTableData(null);
      setIsTemplate('');
      setRespuestaJson(null);
      setIsDashboard(false);
      setExamsOnly(null);
      setShowChart(true);
      return true;
    }

    if (chartDataPayload?.type === 'ranking-table') {
      const rows = Array.isArray(chartDataPayload.rows) ? chartDataPayload.rows : [];
      if (rows.length === 0) {
        return false;
      }
      const rankingPayload: RankingTableData = {
        type: 'ranking-table',
        title: chartDataPayload.title || '',
        subtitle: chartDataPayload.subtitle,
        courses: Array.isArray(chartDataPayload.courses) ? chartDataPayload.courses : [],
        rows,
        metric: chartDataPayload.metric,
        metricLabel: chartDataPayload.metricLabel,
        scope: chartDataPayload.scope,
      };
      setRankingTableData(rankingPayload);
      setOverviewBarsData(null);
      setUniversityFunnelsData(null);
      setTitulacionBarsData(null);
      setChartLabels([]);
      setChartData([]);
      setChartTitle('');
      settipoGrafica(undefined);
      setNumConsultas(1);
      setTableData(null);
      setIsTemplate('');
      setRespuestaJson(null);
      setIsDashboard(false);
      setExamsOnly(null);
      setShowChart(true);
      return true;
    }

    if (chartDataPayload?.type !== 'overview-bars') {
      return false;
    }

    const items = Array.isArray(chartDataPayload.items)
      ? chartDataPayload.items.filter((item: any) => item?.values && typeof item.values === 'object')
      : [];

    if (items.length === 0) {
      return false;
    }

    const metrics = Array.isArray(chartDataPayload.metrics) && chartDataPayload.metrics.length > 0
      ? chartDataPayload.metrics.map((m: any) => ({ metric: String(m?.metric || ''), label: String(m?.label || m?.metric || '') }))
      : Object.keys(items[0].values).map((metric) => ({ metric, label: metric }));

    const normalizedMetrics = metrics.filter((m: any) => m.metric);

    if (normalizedMetrics.length === 0) {
      return false;
    }

    const overviewPayload: OverviewBarsData = {
      type: 'overview-bars',
      title: chartDataPayload.title || 'Resumen',
      subtitle: chartDataPayload.subtitle,
      course: chartDataPayload.course,
      dimension: chartDataPayload.dimension,
      items: items.map((item: any) => ({
        name: item.name || item.label || '',
        label: item.label || item.name || '',
        campus: item.campus,
        values: item.values,
      })),
      metrics: normalizedMetrics,
      scope: chartDataPayload.scope,
    };

    setOverviewBarsData(overviewPayload);
    setUniversityFunnelsData(null);
    setTitulacionBarsData(null);
    setRankingTableData(null);
    setChartLabels([]);
    setChartData([]);
    setChartTitle('');
    settipoGrafica(undefined);
    setNumConsultas(1);
    setTableData(null);
    setIsTemplate('');
    setRespuestaJson(null);
    setIsDashboard(false);
    setExamsOnly(null);
    setShowChart(true);

    return true;
  };

  const [respuestaJson, setRespuestaJson] = useState<DashboardData | null>(null);
  const [isDashboard, setIsDashboard] = useState<boolean>(false);
  const [universityFunnelsData, setUniversityFunnelsData] = useState<UniversityFunnelsData | null>(null);
  const [titulacionBarsData, setTitulacionBarsData] = useState<TitulacionBarsData | null>(null);
  const [overviewBarsData, setOverviewBarsData] = useState<OverviewBarsData | null>(null);
  const [rankingTableData, setRankingTableData] = useState<RankingTableData | null>(null);
  const [personCardsData, setPersonCardsData] = useState<PersonCardsData | null>(null);
  const [departmentOrgChartData, setDepartmentOrgChartData] = useState<DepartmentOrgChartData | null>(null);
  const [examsOnly, setExamsOnly] = useState<DashboardData['examenes'] | null>(null);

  // useRef
  const talkVideoRef = useRef<HTMLVideoElement>(null);
  const didStreamRef = useRef<MediaStream | null>(null);
  const didStreamReadyRef = useRef<boolean>(false);
  const agentManagerRef = useRef<AgentManager | null>(null);
  const didPeerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const isDynamicDidStreamRef = useRef<boolean>(false);
  const streamIdRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const conversationSessionIdRef = useRef<string | null>(null);
  const feedbackSessionIdRef = useRef<string | null>(null);
  const conversationStoppedForFeedbackRef = useRef<boolean>(false);
  const activeTalkRequestRef = useRef<number>(0);
  const interactionLimitSessionRef =
    useRef<AvatarInteractionLimitSession | null>(null);
  const speechRecognizerRef = useRef<speechsdk.SpeechRecognizer | null>(null);
  const speechSynthesizerRef = useRef<speechsdk.SpeechSynthesizer | null>(null);
  const speechPlayerRef = useRef<speechsdk.SpeakerAudioDestination | null>(null);
  const speechTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const speechCompletionWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dynamicDidFinishTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const speechSubmitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didConnectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didSpeakStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const avatarResponseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoStallIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const speechPlaybackStartedRef = useRef<boolean>(false);
  const activeSpeechTextRef = useRef<string>('');
  const audioFallbackModeRef = useRef<boolean>(false);
  const isAvatarSpeakingRef = useRef<boolean>(false);
  const userPausedRef = useRef<boolean>(false);
  const hasConnectedOnceRef = useRef<boolean>(false);
  const hasCheckedMicPermissionsRef = useRef<boolean>(false);
  const micRecoveryAttemptedRef = useRef<boolean>(false);
  const micCreatingRef = useRef<boolean>(false);
  const micStartingRef = useRef<boolean>(false);
  const micStopAfterStartRef = useRef<boolean>(false);
  const micStartedOnceRef = useRef<boolean>(false);
  const isRetryingVideoRef = useRef<boolean>(false);
  const pendingSpeakTalkRef = useRef<AzureTalk | null>(null);
  const heldSpeakTalkRef = useRef<AzureTalk | null>(null);
  const didSpeechStartedRef = useRef<boolean>(false);
  const suppressDidSpeechRef = useRef<boolean>(false);
  const retryVideoOnResumeRef = useRef<boolean>(false);
  const didConnectionGenerationRef = useRef<number>(0);
  const connectingAgentRef = useRef<AgentManager | null>(null);
  const speechBufferRef = useRef<string[]>([]);

  // const
  const streamingMode: boolean = initialConfig?.avatarMode === 'streaming';
  const streamingOnlyMode: boolean =
    initialConfig?.avatarMode === 'streaming_only';
  // const chatOnlyMode: boolean = initialConfig?.avatarMode === 'chat_only';
  const voiceOnlyMode: boolean = initialConfig?.avatarMode === 'chat_voice';
  const voiceSpeechMode: boolean =
    initialConfig?.avatarMode === 'chat_voice_speech';
  const avatarLayoutClassName = [
    'avatar-component',
    'avatar-component-des',
    showChart ? 'avatar-component--with-chart' : '',
    streamingMode ? 'avatar-component--streaming' : '',
    streamingOnlyMode ? 'avatar-component--streaming-only' : '',
    !streamingMode && !streamingOnlyMode ? 'avatar-component--chat-only' : '',
    hasFirstMessageSent ? 'chat-darkened' : '',
  ].filter(Boolean).join(' ');
  const userName = user?.name || usuario || 'Me';
  const [role, setRole] = useState<string>(user?.role || 'Usuario');

  //TAGS
  const [tags, setTags] = useState<string[]>([]);
  const [tagFrases, setTagFrases] = useState<{ [key: string]: string[] }>({});
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  //const [showCookieBanner, setShowCookieBanner] = useState<boolean>(false);
  // Control de acceso a entorno de desarrollo
  const [canAccessDes, setCanAccessDes] = useState<boolean>(false);
  // ENTORNO: 'des' (desarrollo) o 'prod' (producción), persistido en localStorage
  const [environment, setEnvironment] = useState<'des' | 'prod'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('environment');
      if (saved === 'des' || saved === 'prod') {
        return saved;
      }
    }
    return 'prod';
  });

  const updateEnvironment = (env: 'des' | 'prod') => {
    setEnvironment(env);
    if (typeof window !== 'undefined') {
      localStorage.setItem('environment', env);
    }
  };

  // Precarga el mapa NombreProceso -> ID del CSV del entorno activo (usado al pulsar tags)
  useEffect(() => {
    getProcesosIdMap(environment).catch((error) => {
      console.warn('No se pudo cargar el CSV de procesos:', error);
    });
  }, [environment]);

  // (Estados fullscreen deshabilitados)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (avatarType) {
      (async () => {
        showToast('Cargando configuración inicial...', 'info');
        const initialConfig = await getInitialConfig();
        if (initialConfig) {
          showToast(`Configuración de "${avatarType}" cargada.`, 'success');
          // initialConfig.avatarMode = 'streaming_only';
          setInitialConfig(initialConfig);
        } else {
          setErrorOnInitialConfig(
            `No se pudo cargar o no existe el avatar "${avatarType}".`
          );
          showToast(`No se pudo cargar "${avatarType}".`, 'error');
        }
      })();
    }
  }, []);

  useEffect(() => {
    if (!initialConfig) {
      return;
    }

    interactionLimitSessionRef.current = new AvatarInteractionLimitSession(
      isInterviewAvatar
        ? ensureMinimumInteractions(
            (() => {
              const maxInteractions = normalizeMaxInteractions(
                initialConfig.maxInteracciones
              );
              return maxInteractions === null ? null : maxInteractions + 1;
            })(),
            MIN_INTERVIEW_TOTAL_INTERACTIONS
          )
        : normalizeMaxInteractions(initialConfig.maxInteracciones)
    );
    setInteractionLimitReached(false);
    isWaitingResponse = false;
  }, [initialConfig]);

  useEffect(() => {
    if (!initialConfig) {
      return;
    }

    // Adelanta la petición del token para que la primera activación de voz no
    // tenga que esperar también al intercambio de autenticación.
    void getAzureVoiceToken().catch((error) => {
      console.warn('No se pudo precalentar el token de Azure Speech:', error);
    });

    [
      process.env.REACT_APP_DID_API,
      process.env.REACT_APP_CEU_API,
      getUrlOrigin(initialConfig.avatarImgUrl),
      getUrlOrigin(initialConfig.didIdleVideoUrl),
      'https://player.vimeo.com',
      'https://i.vimeocdn.com',
    ]
      .filter(Boolean)
      .forEach((origin) => addResourceHint('preconnect', origin as string));

    if (initialConfig.avatarImgUrl) {
      addResourceHint('preload', initialConfig.avatarImgUrl, 'image');
      const avatarImage = new Image();
      avatarImage.decoding = 'async';
      avatarImage.src = initialConfig.avatarImgUrl;
    }
  }, [initialConfig]);

  useEffect(() => {
    if (
      !initialConfig ||
      isAudioFallbackMode ||
      !(streamingMode || streamingOnlyMode) ||
      !shouldUseDidStreaming() ||
      streamingState === ConnectionState.Connected
    ) {
      setShowAudioFallbackOffer(false);
      return;
    }

    const fallbackOfferTimeout = setTimeout(() => {
      setShowAudioFallbackOffer(true);
    }, AUDIO_FALLBACK_OFFER_DELAY_MS);

    return () => clearTimeout(fallbackOfferTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfig, isAudioFallbackMode, streamingState]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // RESET
  useEffect(() => {
    (async () => {
      if (userToken && user && initialConfig) {
        const initialRes = await getGptData('--reset', userToken, user, undefined, environment, getUrlDominio());

        if (initialRes?.respuesta_corta?.tags) {
          const extractedTags = initialRes.respuesta_corta.tags.map(tag => tag.Tag);
          setTags(extractedTags);
          const extractedTagFrases = initialRes.respuesta_corta.tags.reduce((acc, tag) => {
            acc[tag.Tag] = tag.frases || [];
            return acc;
          }, {} as { [key: string]: string[] });
          setTagFrases(extractedTagFrases);
        }

        if (initialRes?.respuesta_corta?.Rol) {
          setRole(initialRes.respuesta_corta.Rol);
        }

        if (initialRes?.respuesta_corta?.entorno) {
          const entorno = initialRes.respuesta_corta.entorno.toLowerCase() === 'des' ? 'des' : 'prod';
          updateEnvironment(entorno);
        }

        if (initialRes?.respuesta_corta?.acceso_des === true) {
          setCanAccessDes(true);
        } else {
          setCanAccessDes(false);
          if (environment === 'des') {
            updateEnvironment('prod');
            window.location.reload();
            return;
          }
        }

        if (!streamingMode && !streamingOnlyMode) {
          // ...
        }

        const currentDominio = getUrlDominio();

        if (currentDominio) {
          const initialMessageByDominio: Record<string, string> = {
            'it': 'Tengo un problema',
            'biblioteca uch': 'Quiero información sobre la biblioteca',
            'becascolab': "Quiero solicitar una beca de colaboración",
            'becascolabresponsables': 'realizar seleccion de candidatos'
          };
          const initialMessage = initialMessageByDominio[currentDominio.toLowerCase()] ?? 'hola';
          isWaitingResponse = false;
          await talk(initialMessage);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfig, userToken, user]);

  // LOG para debug del Dashboard
  /*useEffect(() => {
    console.log('📱 Dashboard States Debug:');
    console.log('  - showChart:', showChart);
    console.log('  - respuestaCorta:', respuestaCorta);
    console.log('  - respuestaJson:', respuestaJson);
    console.log('  - tipoGrafica:', tipoGrafica);
  }, [showChart, respuestaCorta, respuestaJson, tipoGrafica]);*/

  const getInitials = (name: string) => {
    const [firstName, lastName] = name.split(' ');
    const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
    return initials.toUpperCase();
  };

  const userInitials = getInitials(userName);

  const feedbackAvatarType = avatarType?.toLowerCase() || '';
  const canRequestAvatarFeedback =
    feedbackAvatarType.startsWith('sim_') || feedbackAvatarType.startsWith('ent_');
  const avatarFeedbackType = feedbackAvatarType.startsWith('sim_')
    ? 'simulamnesis'
    : 'entrevista';

  // El backend guarda el historial por session_id: debe mantenerse aunque el
  // vídeo se reconecte (nuevo stream_id) o se pase a modo audio (sin stream).
  const getConversationSessionId = (): string => {
    if (!conversationSessionIdRef.current) {
      conversationSessionIdRef.current =
        streamIdRef.current ||
        `audio_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    }
    return conversationSessionIdRef.current;
  };

  const clearPendingSpeechSubmit = () => {
    if (speechSubmitTimeoutRef.current) {
      clearTimeout(speechSubmitTimeoutRef.current);
      speechSubmitTimeoutRef.current = null;
    }
  };

  const clearSpeechBuffer = () => {
    clearPendingSpeechSubmit();
    speechBufferRef.current = [];
  };

  const clearDidConnectionTimeout = () => {
    if (didConnectionTimeoutRef.current) {
      clearTimeout(didConnectionTimeoutRef.current);
      didConnectionTimeoutRef.current = null;
    }
  };

  const clearDidSpeakStartTimeout = () => {
    if (didSpeakStartTimeoutRef.current) {
      clearTimeout(didSpeakStartTimeoutRef.current);
      didSpeakStartTimeoutRef.current = null;
    }
  };

  const clearAvatarResponseTimeout = () => {
    if (avatarResponseTimeoutRef.current) {
      clearTimeout(avatarResponseTimeoutRef.current);
      avatarResponseTimeoutRef.current = null;
    }
  };

  const clearVideoStallMonitor = () => {
    if (videoStallIntervalRef.current) {
      clearInterval(videoStallIntervalRef.current);
      videoStallIntervalRef.current = null;
    }
  };

  const setAvatarSpeaking = (speaking: boolean) => {
    isAvatarSpeakingRef.current = speaking;
    setIsAvatarSpeaking(speaking);
  };

  const clearSpeechCompletionWatchdog = () => {
    if (speechCompletionWatchdogRef.current) {
      clearTimeout(speechCompletionWatchdogRef.current);
      speechCompletionWatchdogRef.current = null;
    }
  };

  const clearDidSpeechWatch = () => {
    clearDidSpeakStartTimeout();
    clearAvatarResponseTimeout();
    clearVideoStallMonitor();
    pendingSpeakTalkRef.current = null;
    didSpeechStartedRef.current = false;
  };

  const stopAvatarSpeech = () => {
    clearDidSpeechWatch();
    clearSpeechCompletionWatchdog();

    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = null;
    }

    if (dynamicDidFinishTimeoutRef.current) {
      clearTimeout(dynamicDidFinishTimeoutRef.current);
      dynamicDidFinishTimeoutRef.current = null;
    }

    setAvatarSpeaking(false);
    releaseAudioPlayback();
  };

  // Para y libera el reproductor y el sintetizador de Azure. Las referencias se
  // vacían antes de cerrar para que sus callbacks tardíos no afecten al turno
  // siguiente.
  const releaseAudioPlayback = () => {
    const player = speechPlayerRef.current as
      | (speechsdk.SpeakerAudioDestination & {
          close?: () => void;
        })
      | null;
    speechPlayerRef.current = null;
    if (player) {
      try {
        player.pause();
        player.mute();
        player.close?.();
      } catch (error) {
        console.warn('Error al pausar el reproductor de voz:', error);
      }
    }

    const synthesizer = speechSynthesizerRef.current as any;
    speechSynthesizerRef.current = null;
    if (synthesizer) {
      try {
        if (typeof synthesizer.stopSpeakingAsync === 'function') {
          synthesizer.stopSpeakingAsync(
            () => {
              try {
                synthesizer.close();
              } catch {}
            },
            () => {
              try {
                synthesizer.close();
              } catch {}
            }
          );
        } else {
          synthesizer.close();
        }
      } catch (error) {
        console.warn('Error al detener la síntesis de voz:', error);
      }
    }
  };

  // Contador de frames pintados; null si el navegador no lo expone.
  const readVideoFrameCount = (video: HTMLVideoElement): number | null => {
    const quality = video.getVideoPlaybackQuality?.();
    if (quality && typeof quality.totalVideoFrames === 'number') {
      return quality.totalVideoFrames;
    }
    const webkitFrames = (video as HTMLVideoElement & { webkitDecodedFrameCount?: number })
      .webkitDecodedFrameCount;
    return typeof webkitFrames === 'number' ? webkitFrames : null;
  };

  const markDidSpeechStarted = () => {
    if (didSpeechStartedRef.current) {
      return;
    }
    didSpeechStartedRef.current = true;
    clearDidSpeakStartTimeout();
    setAvatarSpeaking(true);
  };

  // Vigila los frames del vídeo de D-ID mientras el avatar habla. En el stream
  // dinámico D-ID deja de enviar frames al terminar, así que ahí el arranque y
  // el final se deducen de los frames; con el agente, quedarse sin frames
  // entre START y STOP significa que el vídeo se ha congelado.
  const startVideoStallMonitor = (
    isDynamicStream: boolean,
    onStall: () => void
  ) => {
    clearVideoStallMonitor();
    let lastFrames = -1;
    let lastTime = -1;
    let lastProgressAt = Date.now();

    videoStallIntervalRef.current = setInterval(() => {
      const video = talkVideoRef.current;
      const now = Date.now();
      if (!video || !video.srcObject || document.hidden) {
        lastProgressAt = now;
        return;
      }

      const frames = readVideoFrameCount(video) ?? 0;
      const time = video.currentTime;
      const progressed = frames > lastFrames || (frames === 0 && time > lastTime);
      if (lastFrames < 0 || progressed) {
        if (isDynamicStream && lastFrames >= 0 && progressed) {
          markDidSpeechStarted();
        }
        lastFrames = frames;
        lastTime = time;
        lastProgressAt = now;
        return;
      }

      if (didSpeechStartedRef.current && now - lastProgressAt >= VIDEO_STALL_FALLBACK_MS) {
        clearVideoStallMonitor();
        onStall();
      }
    }, VIDEO_STALL_CHECK_INTERVAL_MS);
  };

  const scheduleAvatarResponseTimeout = (speakText: string) => {
    clearAvatarResponseTimeout();
    avatarResponseTimeoutRef.current = setTimeout(() => {
      avatarResponseTimeoutRef.current = null;
      if (conversationStoppedForFeedbackRef.current || audioFallbackModeRef.current) {
        return;
      }

      // El vídeo ha seguido avanzando (si no, habría saltado el detector de
      // congelación) pero D-ID no ha avisado del final: se da por terminada.
      console.warn('[DIDAvatar] respuesta sin evento de finalización; se da por terminada.');
      clearDidSpeechWatch();
      setAvatarSpeaking(false);
      showDidIdleVideo('response-timeout');
      completeAvatarInteraction();
      changeVisualMode('finish');
    }, Math.min(Math.max(estimateSpeechMs(speakText) + 3500, 10000), AVATAR_RESPONSE_TIMEOUT_MS));
  };

  // Vigila una respuesta enviada a D-ID: si no empieza a tiempo o se congela,
  // se reproduce por audio sin perder el turno.
  const startDidSpeechWatch = (speakTalk: AzureTalk, isDynamicStream: boolean) => {
    clearDidSpeechWatch();
    suppressDidSpeechRef.current = false;
    pendingSpeakTalkRef.current = speakTalk;
    scheduleAvatarResponseTimeout(speakTalk.speakText);
    startVideoStallMonitor(isDynamicStream, () => {
      if (isDynamicStream) {
        finishDynamicDidSpeech();
        return;
      }
      console.warn('[DIDAvatar] vídeo congelado durante la respuesta; activando modo audio.');
      activateAudioFallback({ reason: 'video-stall', autoStartMic: false });
    });
    didSpeakStartTimeoutRef.current = setTimeout(() => {
      didSpeakStartTimeoutRef.current = null;
      if (
        conversationStoppedForFeedbackRef.current ||
        audioFallbackModeRef.current ||
        didSpeechStartedRef.current ||
        pendingSpeakTalkRef.current !== speakTalk
      ) {
        return;
      }

      console.warn('[DIDAvatar] D-ID no inició la respuesta a tiempo; activando modo audio.');
      activateAudioFallback({ reason: 'did-speak-start-timeout', autoStartMic: false });
    }, DID_SPEAK_START_TIMEOUT_MS);
  };

  const htmlToPlainText = (value: string): string => {
    if (!value) {
      return '';
    }

    const template = document.createElement('template');
    template.innerHTML = value;
    return (template.content.textContent || template.innerText || value)
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const getTranscriptMessageText = (message: Message): string => {
    const response = message.response;
    const messageText = response?.respuesta_usuario || '';
    const formattedText =
      response?.formato_respuesta_usuario?.toLowerCase() === 'html'
        ? htmlToPlainText(messageText)
        : messageText;
    const attachedFiles = response?.files?.length
      ? `\nArchivos adjuntos: ${response.files.map((file) => file.name).join(', ')}`
      : '';

    return `${formattedText}${attachedFiles}`.trim();
  };

  const escapeHtml = (value: string): string =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const renderTextAsHtml = (value: string): string =>
    escapeHtml(value)
      .split('\n')
      .map((line) => line || '&nbsp;')
      .join('<br />');

  const buildConversationReportHtml = (): string => {
    const avatarName = initialConfig?.avatarName || avatarType || 'Simulaimnesis';
    const studentMessages = messages.filter((message) => message.sender !== 'bot').length;
    const patientMessages = messages.filter((message) => message.sender === 'bot').length;
    const firstMessageTime = messages[0]?.response?.time || 'No disponible';
    const lastMessageTime = messages[messages.length - 1]?.response?.time || 'No disponible';

    const transcriptMessages = messages.map((message, index) => {
      const isPatient = message.sender === 'bot';
      const speaker =
        isPatient
          ? `Paciente${initialConfig?.avatarName ? ` (${initialConfig.avatarName})` : ''}`
          : 'Tú';
      const timestamp = message.response?.time || '';
      const content = getTranscriptMessageText(message) || '(mensaje sin contenido)';

      return `
        <article class="message ${isPatient ? 'message--patient' : 'message--student'}">
          <div class="message__meta">
            <span class="message__number">${index + 1}</span>
            <span class="message__speaker">${escapeHtml(speaker)}</span>
            ${timestamp ? `<time>${escapeHtml(timestamp)}</time>` : ''}
          </div>
          <div class="message__body">${renderTextAsHtml(content)}</div>
        </article>
      `;
    }).join('');

    return `<!doctype html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Informe de conversación - ${escapeHtml(avatarName)}</title>
  <style>
    :root {
      color-scheme: light;
      --ceu-blue: #14213d;
      --ceu-gold: #c8a24a;
      --ink: #1f2937;
      --muted: #667085;
      --line: #e6eaf0;
      --paper: #ffffff;
      --soft: #f6f8fb;
      --student: #eaf2ff;
      --patient: #fff6df;
    }

    * {
      box-sizing: border-box;
    }

    body {
      margin: 0;
      background: #edf1f7;
      color: var(--ink);
      font-family: "Inter", "Segoe UI", Arial, sans-serif;
      line-height: 1.55;
    }

    .page {
      max-width: 980px;
      margin: 32px auto;
      background: var(--paper);
      border-radius: 28px;
      box-shadow: 0 24px 70px rgba(20, 33, 61, 0.14);
      overflow: hidden;
    }

    .hero {
      background: linear-gradient(135deg, var(--ceu-blue), #264574);
      color: #fff;
      padding: 38px 42px;
      position: relative;
    }

    .hero::after {
      content: "";
      position: absolute;
      right: -90px;
      top: -120px;
      width: 280px;
      height: 280px;
      border-radius: 50%;
      background: rgba(200, 162, 74, 0.2);
    }

    .eyebrow {
      display: inline-flex;
      gap: 8px;
      align-items: center;
      margin: 0 0 16px;
      color: #f4d98c;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    h1 {
      margin: 0;
      max-width: 720px;
      font-size: clamp(30px, 5vw, 46px);
      line-height: 1.05;
    }

    .hero__subtitle {
      max-width: 680px;
      margin: 16px 0 0;
      color: rgba(255, 255, 255, 0.82);
      font-size: 17px;
    }

    .content {
      padding: 34px 42px 44px;
    }

    .summary-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px;
      margin-bottom: 28px;
    }

    .summary-card {
      padding: 18px;
      background: var(--soft);
      border: 1px solid var(--line);
      border-radius: 18px;
    }

    .summary-card__label {
      display: block;
      color: var(--muted);
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .summary-card__value {
      display: block;
      margin-top: 7px;
      font-size: 18px;
      font-weight: 750;
    }

    .section-title {
      margin: 34px 0 14px;
      color: var(--ceu-blue);
      font-size: 22px;
    }

    .note {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 14px;
      padding: 18px;
      margin-bottom: 26px;
      background: #fffbeb;
      border: 1px solid #f4d98c;
      border-radius: 18px;
    }

    .note__icon {
      display: grid;
      place-items: center;
      width: 34px;
      height: 34px;
      border-radius: 999px;
      background: var(--ceu-gold);
      color: #fff;
      font-weight: 800;
    }

    .note p {
      margin: 0;
      color: #5b4713;
    }

    .timeline {
      display: grid;
      gap: 16px;
    }

    .message {
      border: 1px solid var(--line);
      border-radius: 20px;
      overflow: hidden;
    }

    .message--student {
      background: var(--student);
    }

    .message--patient {
      background: var(--patient);
    }

    .message__meta {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      align-items: center;
      padding: 13px 16px;
      background: rgba(255, 255, 255, 0.62);
      border-bottom: 1px solid rgba(20, 33, 61, 0.08);
      color: var(--muted);
      font-size: 13px;
      font-weight: 650;
    }

    .message__number {
      display: inline-grid;
      place-items: center;
      width: 28px;
      height: 28px;
      border-radius: 999px;
      background: var(--ceu-blue);
      color: #fff;
      font-size: 12px;
    }

    .message__speaker {
      color: var(--ceu-blue);
      font-size: 15px;
      font-weight: 800;
    }

    .message__body {
      padding: 18px 20px 20px;
      font-size: 16px;
    }

    .footer {
      padding-top: 26px;
      margin-top: 34px;
      border-top: 1px solid var(--line);
      color: var(--muted);
      font-size: 13px;
    }

    @media (max-width: 760px) {
      .page {
        margin: 0;
        border-radius: 0;
      }

      .hero,
      .content {
        padding-left: 22px;
        padding-right: 22px;
      }

      .summary-grid {
        grid-template-columns: 1fr 1fr;
      }
    }

    @media print {
      body {
        background: #fff;
      }

      .page {
        margin: 0;
        border-radius: 0;
        box-shadow: none;
      }

      .hero {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .message {
        break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <main class="page">
    <header class="hero">
      <p class="eyebrow">Simulaimnesis · Conversación clínica</p>
      <h1>Informe de conversación con paciente</h1>
      <p class="hero__subtitle">Documento de repaso para analizar la entrevista, revisar las preguntas realizadas y preparar mejoras para futuras simulaciones.</p>
    </header>

    <section class="content">
      <div class="summary-grid" aria-label="Resumen de la sesión">
        <div class="summary-card">
          <span class="summary-card__label">Paciente</span>
          <span class="summary-card__value">${escapeHtml(avatarName)}</span>
        </div>
        <div class="summary-card">
          <span class="summary-card__label">Mensajes</span>
          <span class="summary-card__value">${messages.length}</span>
        </div>
      </div>

      <div class="summary-grid" aria-label="Detalle de participación">
        <div class="summary-card">
          <span class="summary-card__label">Tus intervenciones</span>
          <span class="summary-card__value">${studentMessages}</span>
        </div>
        <div class="summary-card">
          <span class="summary-card__label">Intervenciones paciente</span>
          <span class="summary-card__value">${patientMessages}</span>
        </div>
        <div class="summary-card">
          <span class="summary-card__label">Inicio</span>
          <span class="summary-card__value">${escapeHtml(firstMessageTime)}</span>
        </div>
        <div class="summary-card">
          <span class="summary-card__label">Fin</span>
          <span class="summary-card__value">${escapeHtml(lastMessageTime)}</span>
        </div>
      </div>

      <div class="note">
        <div class="note__icon">i</div>
        <p>Utiliza este informe como material de aprendizaje: identifica qué información clínica obtuviste, qué datos faltaron por preguntar y cómo podrías ordenar mejor la entrevista.</p>
      </div>

      <h2 class="section-title">Transcripción</h2>
      <section class="timeline">
        ${transcriptMessages}
      </section>

      <footer class="footer">
        Documento generado automáticamente por CEU Avatar.
      </footer>
    </section>
  </main>
</body>
</html>`;
  };

  const downloadHtmlAsPdf = async (reportHtml: string, filename: string) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '1100px';
    iframe.style.height = '1600px';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');

    document.body.appendChild(iframe);

    try {
      await new Promise<void>((resolve, reject) => {
        iframe.onload = () => resolve();
        iframe.onerror = () => reject(new Error('No se pudo preparar el informe PDF.'));
        iframe.srcdoc = reportHtml;
      });

      const reportElement = iframe.contentDocument?.querySelector('.page') as HTMLElement | null;
      if (!reportElement) {
        throw new Error('No se pudo renderizar el informe PDF.');
      }

      const canvas = await html2canvas(reportElement, {
        backgroundColor: '#edf1f7',
        scale: 2,
        useCORS: true,
        windowWidth: reportElement.scrollWidth,
        windowHeight: reportElement.scrollHeight,
      });

      const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imageWidth = pageWidth;
      const imageHeight = (canvas.height * imageWidth) / canvas.width;
      const imageData = canvas.toDataURL('image/png');

      let yPosition = 0;
      let remainingHeight = imageHeight;

      pdf.addImage(imageData, 'PNG', 0, yPosition, imageWidth, imageHeight);
      remainingHeight -= pageHeight;

      while (remainingHeight > 0) {
        yPosition -= pageHeight;
        pdf.addPage();
        pdf.addImage(imageData, 'PNG', 0, yPosition, imageWidth, imageHeight);
        remainingHeight -= pageHeight;
      }

      pdf.save(filename);
    } finally {
      document.body.removeChild(iframe);
    }
  };

  const downloadSimulaimnesisConversation = async () => {
    if (!messages.length) {
      showToast('No hay mensajes para descargar todavía.', 'warn');
      return;
    }

    try {
      const reportHtml = buildConversationReportHtml();
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const avatarName = (initialConfig?.avatarName || avatarType || 'simulaimnesis')
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[<>:"/\\|?*]+/g, '');

      await downloadHtmlAsPdf(
        reportHtml,
        `informe-conversacion-${avatarName}-${timestamp}.pdf`
      );
      showToast('Informe de conversación descargado en PDF.', 'success');
    } catch (error) {
      console.error('Error generando el PDF de conversación:', error);
      showToast('No se pudo generar el PDF de la conversación.', 'error');
    }
  };

  const stopConversationForFeedback = () => {
    conversationStoppedForFeedbackRef.current = true;
    setIsConversationStopped(true);
    activeTalkRequestRef.current += 1;
    cancelAvatarInteraction();
    clearSpeechBuffer();
    stopAvatarSpeech();
    heldSpeakTalkRef.current = null;
    isWaitingResponse = false;
    setIsBotThinking(false);
    setIsChatSent(false);
    setDisplayText('Conversación pausada para solicitar feedback');

    destroyMic(true);
    if (speechRecognizerRef.current) {
      speechRecognizerRef.current.close();
      speechRecognizerRef.current = null;
      speechRef = null;
    }
    disconnectAgent();
  };

  const handleRequestFeedback = async () => {
    const botMessageCount = messages.filter(m => m.sender === 'bot').length;
    if (
      isInterviewAvatar &&
      botMessageCount < MIN_INTERVIEW_TOTAL_INTERACTIONS
    ) {
      showToast(
        'La entrevista debe tener al menos 3 interacciones, además de la presentación.',
        'warn'
      );
      return;
    }

    const feedbackSessionId = getConversationSessionId();
    if (!feedbackSessionId) {
      showToast('No hay una conversación activa para enviar feedback.', 'warn');
      return;
    }

    feedbackSessionIdRef.current = feedbackSessionId;
    stopConversationForFeedback();
    setFeedbackEmailInput(user?.email || '');
    setShowFeedbackModal(true);
  };

  const submitFeedbackRequest = async () => {
    const feedbackEmail = feedbackEmailInput.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(feedbackEmail)) {
      showToast('Introduce un correo válido para recibir el feedback.', 'warn');
      return;
    }

    const feedbackSessionId =
      feedbackSessionIdRef.current || getConversationSessionId();
    if (!feedbackSessionId || !avatarType) {
      showToast('No hay una sesión válida para solicitar el feedback.', 'error');
      return;
    }

    setIsFeedbackSending(true);
    const result = await sendAvatarFeedback({
      session_id: feedbackSessionId,
      avatar: avatarType,
      usuario: user?.name || usuario,
      email: feedbackEmail,
      tipo: avatarFeedbackType,
    });
    setIsFeedbackSending(false);

    if (result.ok) {
      setFeedbackRequested(true);
      setShowFeedbackModal(false);
      showToast(
        result.message || 'Feedback solicitado. Recibirás el PDF por correo.',
        'success'
      );
    } else {
      showToast(result.message || 'No se pudo solicitar el feedback.', 'error');
    }
  };

  const FeedbackButton = () => {
    const botMessageCount = messages.filter(m => m.sender === 'bot').length;
    if (
      !canRequestAvatarFeedback ||
      !hasFirstMessageSent ||
      botMessageCount < MIN_INTERVIEW_TOTAL_INTERACTIONS
    ) {
      return null;
    }

    return (
      <button
        type="button"
        className="avatar-feedback-button"
        onClick={handleRequestFeedback}
        disabled={isFeedbackSending || feedbackRequested}
      >
        {isFeedbackSending
          ? 'Solicitando feedback...'
          : feedbackRequested
            ? 'Feedback solicitado'
            : 'Recibir feedback por email'}
      </button>
    );
  };

  const ConversationDownloadButton = () => {
    if (!isSimulaimnesisAvatar() || !hasFirstMessageSent || messages.length === 0) {
      return null;
    }

    return (
      <button
        type="button"
        className="avatar-feedback-button avatar-download-conversation-button"
        onClick={downloadSimulaimnesisConversation}
      >
        Descargar conversación
      </button>
    );
  };

  const FeedbackSuccessOverlay = () => {
    if (!feedbackRequested) {
      return null;
    }

    return (
      <div className="feedback-success-overlay">
        <div className="feedback-success-card">
          <div className="feedback-success-card__icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <h2 className="feedback-success-card__title">Feedback enviado</h2>
          <p className="feedback-success-card__message">
            Recibirás un informe detallado en tu correo electrónico en los próximos minutos.
          </p>
          <p className="feedback-success-card__email">
            Enviado a: <strong>{feedbackEmailInput}</strong>
          </p>
        </div>
      </div>
    );
  };

  const FeedbackEmailModal = () => {
    if (!showFeedbackModal) {
      return null;
    }

    return (
      <div className="feedback-modal-backdrop" role="presentation">
        <form
          className="feedback-modal"
          onSubmit={(event) => {
            event.preventDefault();
            submitFeedbackRequest();
          }}
        >
          <div className="feedback-modal__brand">
            <div className="feedback-modal__logo" aria-label="CEU" />
          </div>
          <h2>Enviar feedback en PDF</h2>
          <p>
            Indica el correo donde quieres recibir el informe de esta simulación.
          </p>
          <label className="feedback-modal__label" htmlFor="feedback-email">
            Correo electrónico
          </label>
          <input
            id="feedback-email"
            className="feedback-modal__input"
            type="email"
            value={feedbackEmailInput}
            onChange={(event) => setFeedbackEmailInput(event.target.value)}
            placeholder="nombre@uchceu.es"
            autoComplete="email"
            autoFocus
          />
          <div className="feedback-modal__actions">
            <button
              type="button"
              className="feedback-modal__button feedback-modal__button--secondary"
              onClick={() => setShowFeedbackModal(false)}
              disabled={isFeedbackSending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="feedback-modal__button feedback-modal__button--primary"
              disabled={isFeedbackSending}
            >
              {isFeedbackSending ? 'Enviando...' : 'Enviar feedback'}
            </button>
          </div>
        </form>
      </div>
    );
  };


  // Helpers para vista sólo de exámenes
  type NumericLikeLocal = number | string | null | undefined;
  const toNumberLocal = (value: NumericLikeLocal, fallback = 0) => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : fallback;
    }
    if (typeof value === 'string') {
      const normalized = value.replace(',', '.');
      const parsed = parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : fallback;
    }
    return fallback;
  };

  const getRandomPhrase = (frases: string[]): string => {
    if (frases.length === 0) return '';
    const randomIndex = Math.floor(Math.random() * frases.length);
    return frases[randomIndex];
  };

  // Parar el reconocedor mientras Azure todavía está abriendo el micrófono
  // rompe el SDK ("Cannot read properties of null (reading 'reject')"):
  // las paradas pedidas durante el arranque se aplican cuando termina.
  const initializeOrResumeMic = async (speechRecognizer: any) => {
    if (!speechRecognizer || isMicOpened || micStartingRef.current) {
      return;
    }
    micStartingRef.current = true;
    micStopAfterStartRef.current = false;
    if (!hasCheckedMicPermissionsRef.current) {
      hasCheckedMicPermissionsRef.current = true;
      await checkMicPermissions();
    }
    speechRecognizer.startContinuousRecognitionAsync(
      () => {
        micStartingRef.current = false;
        onStartContinuousRecognition(speechRecognizer);
      },
      (err: string) => {
        micStartingRef.current = false;
        onStartContinuousRecognitionError(err);
      }
    );
  };

  const pauseMic = () => {
    clearSpeechBuffer();
    if (micStartingRef.current) {
      micStopAfterStartRef.current = true;
      return;
    }
    if (speechRef) {
      isMicOpened = false;
      speechRef.stopContinuousRecognitionAsync();
    }
  };

  const destroyMic = (defaultState: boolean = false) => {
    clearSpeechBuffer();
    if (micStartingRef.current) {
      micStopAfterStartRef.current = true;
      if (defaultState) {
        setTimeout(() => {
          setIsMicEnabled(undefined);
        }, 200);
      }
      return;
    }
    if (speechRef) {
      isMicOpened = false;
      speechRef.stopContinuousRecognitionAsync();
      if (defaultState) {
        setTimeout(() => {
          setIsMicEnabled(undefined);
        }, 200);
      }
    }
  };

  const completeAvatarInteraction = () => {
    clearAvatarResponseTimeout();
    const session = interactionLimitSessionRef.current;
    if (!session?.complete()) {
      return;
    }

    if (session.reachedLimit) {
      clearSpeechBuffer();
      destroyMic();
      setIsMicEnabled(false);
      setIsChatSent(false);
      setInteractionLimitReached(true);
      setDisplayText('Se ha alcanzado el límite de la sesión.');
    }
  };

  const cancelAvatarInteraction = () => {
    clearAvatarResponseTimeout();
    interactionLimitSessionRef.current?.cancel();
  };

  const changeVisualMode = (mode: string) => {
    if (conversationStoppedForFeedbackRef.current && mode !== 'reset') {
      isMicOpened = false;
      setIsMicEnabled(false);
      setIsChatSent(false);
      return;
    }
    if (
      interactionLimitSessionRef.current?.reachedLimit &&
      mode !== 'reset'
    ) {
      isMicOpened = false;
      setIsMicEnabled(false);
      setIsChatSent(false);
      return;
    }

    const resetStates = () => {
      isMicOpened = false;
      setDisplayText('Pulsa para conectar');
      setIsChatSent(false);
      setIsMicEnabled(undefined);
    };
    switch (mode) {
      case 'finish':
        if (userPausedRef.current) {
          setDisplayText('En pausa');
        } else if (speechRef || streamingMode || streamingOnlyMode) {
          void openMic();
        }
        if (!streamingMode && !streamingOnlyMode) {
          setIsChatSent(false);
        }
        break;
      case 'reset':
        resetStates();
        break;
      case 'failed':
        resetStates();
        setDisplayText(
          'No se pudo conectar con los servicios de streaming. Pulsa para reintentarlo'
        );
        break;
      case 'open-mic':
        if (!isMicOpened) {
          initializeOrResumeMic(speechRef);
        }
        break;
      case 'close-mic':
        if (isMicOpened) {
          pauseMic();
        }
        break;
      default:
        break;
    }
  };

  const onStartContinuousRecognition = (
    speechRecognizer: speechsdk.SpeechRecognizer | null
  ) => {
    speechRef = speechRecognizer;
    isMicOpened = true;
    micRecoveryAttemptedRef.current = false;
    micStartedOnceRef.current = true;
    setMicStartFailed(false);
    if (
      micStopAfterStartRef.current ||
      userPausedRef.current ||
      conversationStoppedForFeedbackRef.current ||
      isWaitingResponse ||
      isAvatarSpeakingRef.current
    ) {
      // Se pidió cerrar el micro (o el avatar está contestando) mientras arrancaba.
      micStopAfterStartRef.current = false;
      pauseMic();
      return;
    }
    setIsMicEnabled(true);
    setDisplayText('Escuchando...');
    console.log('Micrófono activado.');
  };

  const flushRecognizedSpeech = () => {
    clearPendingSpeechSubmit();
    const userMessage = speechBufferRef.current.join(' ').trim();
    speechBufferRef.current = [];

    if (
      !userMessage ||
      isWaitingResponse ||
      conversationStoppedForFeedbackRef.current ||
      interactionLimitReached
    ) {
      return;
    }

    changeVisualMode('close-mic');
    talk(userMessage);
  };

  const queueRecognizedSpeech = (text: string) => {
    const recognizedText = text.trim();
    if (
      !recognizedText ||
      isWaitingResponse ||
      conversationStoppedForFeedbackRef.current ||
      interactionLimitReached
    ) {
      return;
    }

    speechBufferRef.current.push(recognizedText);
    scheduleSpeechSubmit();
  };

  const scheduleSpeechSubmit = () => {
    clearPendingSpeechSubmit();
    if (speechBufferRef.current.length === 0) {
      return;
    }
    setDisplayText('Te escucho, sigue si quieres...');
    speechSubmitTimeoutRef.current = setTimeout(
      flushRecognizedSpeech,
      SPEECH_SUBMIT_DELAY_MS
    );
  };

  const onStartContinuousRecognitionError = (err: string) => {
    console.log('onStartRecognitionError', err);
    discardSpeechRecognizer();
    hasCheckedMicPermissionsRef.current = false;
    setMicStartFailed(true);
    showToast('No se pudo activar el micrófono. Revisa los permisos del navegador.', 'error');
  };

  const discardSpeechRecognizer = () => {
    clearSpeechBuffer();
    isMicOpened = false;
    try {
      speechRecognizerRef.current?.close();
    } catch {}
    speechRecognizerRef.current = null;
    speechRef = null;
  };

  const setRecognizerMethods = (
    speechRecognizer: speechsdk.SpeechRecognizer
  ) => {
    // El envío solo puede dispararse tras un silencio real: mientras haya
    // resultados parciales el estudiante sigue hablando.
    speechRecognizer.recognizing = (s, e) => {
      if (isMicOpened && e.result.text?.trim()) {
        clearPendingSpeechSubmit();
      }
    };
    speechRecognizer.recognized = (s, e) => {
      // console.log(`ESCUCHANDO...${isMicOpened} ${e.result.text}`);
      if (!isMicOpened) {
        return;
      }
      if (
        e.result.reason === speechsdk.ResultReason.RecognizedSpeech &&
        e.result.text?.trim()
      ) {
        queueRecognizedSpeech(e.result.text);
      } else {
        scheduleSpeechSubmit();
      }
    };
    speechRecognizer.canceled = (s, e) => {
      console.log(
        `Reconocimiento de voz cancelado: ErrorCode=${e.errorCode} Message=${e.errorDetails}`
      );
      if (e.reason === speechsdk.CancellationReason.Error && !micStartingRef.current) {
        // Suele ser un token caducado o un corte de red: el reconocedor ya no
        // sirve, se crea uno nuevo (una vez) para no dejar la conversación muda.
        discardSpeechRecognizer();
        changeVisualMode('reset');
        if (
          !micRecoveryAttemptedRef.current &&
          !userPausedRef.current &&
          !conversationStoppedForFeedbackRef.current &&
          (streamingMode || streamingOnlyMode)
        ) {
          micRecoveryAttemptedRef.current = true;
          setTimeout(() => {
            if (!userPausedRef.current && !isWaitingResponse && !isAvatarSpeakingRef.current) {
              void enableMic();
            }
          }, 800);
        }
      }
    };
    speechRecognizer.sessionStopped = (s, e) => {
      // estados a cambiar cuando el micro se pausa
      isMicOpened = false;
      setIsMicEnabled(false);
      console.log('Micrófono pausado.');
    };
  };

  /*
  const startOnceRecognition = (
    speechRecognizer: speechsdk.SpeechRecognizer
  ) => {
    // TODO: hay que adaptar este modo de escucha
    const onStartRecognition = (result: speechsdk.SpeechRecognitionResult) => {
      console.log('result', result, result.reason, result.text);
      if (result.reason === speechsdk.ResultReason.RecognizedSpeech) {
        // changeVisualMode('close-mic'); // adaptarlo a reconocimiento de voz no contínuo (por botón)
        addNewMessage(result.text, 'user', userName);
        talk(result.text);
      } else if (result.reason === speechsdk.ResultReason.Canceled) {
        changeVisualMode('reset');
        console.log(
          `"CANCELLED: ErrorCode=${result.reason} Message=${result.errorDetails}`
        );
      } else if (result.reason === speechsdk.ResultReason.NoMatch) {
        changeVisualMode('reset');
        setDisplayText('No se ha detectado la voz. Pulsa de nuevo para hablar');
      }
    };
    const onStartRecognitionError = (err: string) => {
      console.log('onStartRecognitionError', err);
      changeVisualMode('reset');
    };
    speechRecognizer.recognizeOnceAsync(
      onStartRecognition,
      onStartRecognitionError
    );
  }; */

  const getSpeechRecognizer = async (): Promise<speechsdk.SpeechRecognizer> => {
    if (speechRecognizerRef.current) {
      return speechRecognizerRef.current;
    }

    const tokenObj = await getAzureVoiceToken();
    const speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(
      tokenObj.authToken,
      tokenObj.region
    );
    speechConfig.speechRecognitionLanguage =
      initialConfig?.speechRecognitionLanguage ?? 'es-ES';
    speechConfig.setProperty(
      speechsdk.PropertyId.Speech_SegmentationSilenceTimeoutMs,
      String(SPEECH_SEGMENTATION_SILENCE_MS)
    );
    const audioConfig = speechsdk.AudioConfig.fromDefaultMicrophoneInput();
    const speechRecognizer = new speechsdk.SpeechRecognizer(
      speechConfig,
      audioConfig
    );
    speechRecognizerRef.current = speechRecognizer;
    return speechRecognizer;
  };

  const enableMic = async () => {
    if (isMicOpened || micStartingRef.current || micCreatingRef.current) {
      return;
    }
    console.log('Creando el microfono...');
    micCreatingRef.current = true;
    setDisplayText('Activando micrófono...');
    const talkingBtn = document.getElementById('talking-btn') as HTMLElement;
    if (talkingBtn) {
      talkingBtn.style.backgroundColor = 'rgba(50, 107, 240, 0.55)';
      talkingBtn.style.borderColor = 'rgba(100, 150, 255, 0.35)';
    }
    try {
      const speechRecognizer = await getSpeechRecognizer();
      setRecognizerMethods(speechRecognizer);
      await initializeOrResumeMic(speechRecognizer);
    } catch (error) {
      console.error('No se pudo crear el micrófono:', error);
      setMicStartFailed(true);
      showToast('No se pudo activar el micrófono. Comprueba tu conexión.', 'error');
    } finally {
      micCreatingRef.current = false;
    }
    // startOnceRecognition(speechRecognizer);
  };

  const openMic = async () => {
    if (isMicOpened || interactionLimitSessionRef.current?.reachedLimit) {
      return;
    }
    if (speechRef) {
      await initializeOrResumeMic(speechRef);
      return;
    }
    await enableMic();
  };

  const sttFromMic = async (isManualClick: boolean = false) => {
    if (interactionLimitReached) {
      showToast('Se ha alcanzado el límite de la sesión.', 'warn');
      return;
    }

    const shouldUseDid =
      shouldUseDidStreaming() && !audioFallbackModeRef.current;
    const isStreamingConnected = streamingState === 'connected';
    let isStreamingInitialized = shouldUseDid ? isStreamingConnected : true;
    if (shouldUseDid && !isStreamingConnected) {
      setDisplayText(
        `Iniciando conversación con ${initialConfig.avatarName || 'el asistente'
        }...`
      );
      const talkingBtn = document.getElementById('talking-btn') as HTMLElement;
      if (talkingBtn) {
        talkingBtn.style.backgroundColor = 'rgba(50, 107, 240, 0.55)';
        talkingBtn.style.borderColor = 'rgba(100, 150, 255, 0.35)';
      }
      // Si falla, el efecto de streamingState pasa a modo audio.
      isStreamingInitialized = Boolean(await connect());
    }
    if (
      (isStreamingInitialized && (isStreamingConnected || !shouldUseDid)) ||
      voiceOnlyMode ||
      voiceSpeechMode
    ) {
      if (isMicEnabled === false) {
        // abre el micro de forma manual
        isManualClick ? destroyMic(true) : changeVisualMode('open-mic');
      } else if (isMicEnabled) {
        // cierra el micro de forma manual
        isManualClick ? destroyMic(true) : changeVisualMode('close-mic');
      } else {
        // habilita el micro cuando vuelves a pulsar, si no se habilita desde el useEffect
        enableMic();
      }
    }
  };

  useEffect(() => {
    if (
      initialConfig &&
      (initialConfig.avatarMode === 'streaming' || initialConfig.avatarMode === 'streaming_only')
    ) {
      if (!shouldUseDidStreaming()) {
        disconnectAgent();
      }
      sttFromMic();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfig]);

  const stopAllStreams = async () => {
    if (talkVideoRef.current?.srcObject) {
      console.log('stopping video streams');
      (talkVideoRef.current.srcObject as MediaStream)
        .getTracks()
        .forEach((track) => track.stop());
      talkVideoRef.current.srcObject = null;
    }
  };

  const getDidApiBaseUrl = (): string =>
    (process.env.REACT_APP_DID_API || 'https://api.d-id.com').replace(/\/$/, '');

  const getDidAuthHeader = (): string | null => {
    const didKey = process.env.REACT_APP_DID_KEY;
    return didKey ? `Basic ${didKey}` : null;
  };

  const didFetch = async <T,>(path: string, init: RequestInit): Promise<T> => {
    const authorization = getDidAuthHeader();
    if (!authorization) {
      throw new Error('Falta configurar REACT_APP_DID_KEY.');
    }

    const response = await fetch(`${getDidApiBaseUrl()}${path}`, {
      ...init,
      headers: {
        Authorization: authorization,
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `D-ID request failed: ${response.status}`);
    }

    return response.json() as Promise<T>;
  };

  const closeDynamicDidStream = async () => {
    const streamId = streamIdRef.current;
    const sessionId = sessionIdRef.current;
    const peerConnection = didPeerConnectionRef.current;
    const wasDynamicStream = isDynamicDidStreamRef.current;

    if (dynamicDidFinishTimeoutRef.current) {
      clearTimeout(dynamicDidFinishTimeoutRef.current);
      dynamicDidFinishTimeoutRef.current = null;
    }

    didPeerConnectionRef.current = null;
    isDynamicDidStreamRef.current = false;
    if (wasDynamicStream) {
      streamIdRef.current = null;
      sessionIdRef.current = null;
    }

    try {
      if (wasDynamicStream && streamId && sessionId) {
        await didFetch(`/talks/streams/${streamId}`, {
          method: 'DELETE',
          body: JSON.stringify({ session_id: sessionId }),
        });
      }
    } catch (error) {
      if (conversationStoppedForFeedbackRef.current) {
        console.info('D-ID stream remoto ya no estaba activo al pedir feedback.');
      } else {
        console.warn('No se pudo cerrar el stream dinámico de D-ID:', error);
      }
    } finally {
      if (peerConnection) {
        peerConnection.close();
      }
    }
  };

  const finishDynamicDidSpeech = () => {
    if (dynamicDidFinishTimeoutRef.current) {
      clearTimeout(dynamicDidFinishTimeoutRef.current);
      dynamicDidFinishTimeoutRef.current = null;
    }
    clearDidSpeechWatch();
    setAvatarSpeaking(false);
    if (conversationStoppedForFeedbackRef.current || audioFallbackModeRef.current) {
      return;
    }
    showDidIdleVideo('dynamic-finish');
    completeAvatarInteraction();
    changeVisualMode('finish');
  };

  const scheduleDynamicDidFinish = (text: string) => {
    if (dynamicDidFinishTimeoutRef.current) {
      clearTimeout(dynamicDidFinishTimeoutRef.current);
    }

    const estimatedSpeechMs = Math.min(estimateSpeechMs(text), 30000);
    dynamicDidFinishTimeoutRef.current = setTimeout(
      finishDynamicDidSpeech,
      estimatedSpeechMs
    );
  };

  const disconnectAgent = async () => {
    clearDidConnectionTimeout();
    clearDidSpeechWatch();
    didConnectionGenerationRef.current += 1;
    const agent = agentManagerRef.current;
    agentManagerRef.current = null;
    didStreamRef.current = null;
    didStreamReadyRef.current = false;
    setIsDidStreamVisible(false);

    stopAllStreams();
    await closeDynamicDidStream();
    // El SDK de D-ID no soporta desconectar mientras connect() todavía tiene
    // una petición pendiente: la generación invalida esa conexión y connect()
    // cerrará ese agente cuando termine.
    if (agent && agent !== connectingAgentRef.current) {
      try {
        await agent.disconnect();
      } catch {
        // D-ID puede fallar al desconectar si está hablando; se ignora
      }
    }
  };

  function isSimulaimnesisAvatar(): boolean {
    const currentAvatarType = avatarType?.toLowerCase() || '';
    return currentAvatarType.startsWith('sim_');
  }

  function shouldUseDynamicDidStreaming(): boolean {
    return (
      isSimulaimnesisAvatar() &&
      !initialConfig?.didAgentId &&
      Boolean(initialConfig?.avatarImgUrl)
    );
  }

  function shouldUseAgentDidStreaming(): boolean {
    return !isSimulaimnesisAvatar() || Boolean(initialConfig?.didAgentId);
  }

  function shouldUseDidStreaming(): boolean {
    return shouldUseAgentDidStreaming() || shouldUseDynamicDidStreaming();
  }

  const getDidIdleVideoUrl = (): string | undefined =>
    initialConfig?.didIdleVideoUrl;

  const showDidIdleVideo = (reason: string): boolean => {
    if (!shouldUseDidStreaming()) {
      setIsDidStreamVisible(false);
      return false;
    }

    if (!talkVideoRef.current) {
      return false;
    }

    const idleVideo = getDidIdleVideoUrl();
    if (!idleVideo) {
      return false;
    }

    const video = talkVideoRef.current;
    video.srcObject = null;
    video.loop = true;
    video.muted = true;
    if (video.src !== idleVideo) {
      video.src = idleVideo;
    }
    setIsDidStreamVisible(true);
    console.info('[DIDAvatar] idle video', { reason, idleVideo });
    playDidVideo(video);
    return true;
  };

  const showDidSpeakingStream = (): boolean => {
    if (!shouldUseDidStreaming()) {
      setIsDidStreamVisible(false);
      return false;
    }

    if (!talkVideoRef.current || !didStreamRef.current) {
      return false;
    }

    const video = talkVideoRef.current;
    video.loop = false;
    video.muted = false;
    video.src = '';
    video.srcObject = didStreamRef.current;
    setIsDidStreamVisible(true);
    playDidVideo(video);
    return true;
  };

  // true: conectado · false: ha fallado · null: cancelado por otra conexión o por el modo audio.
  const connectDynamicDidStream = async (): Promise<boolean | null> => {
    if (!initialConfig?.avatarImgUrl) {
      showToast('Falta la imagen del avatar para generar el vídeo dinámico.', 'error');
      return false;
    }

    await disconnectAgent();
    const connectionGeneration = ++didConnectionGenerationRef.current;
    const isCurrentConnection = () =>
      connectionGeneration === didConnectionGenerationRef.current &&
      !audioFallbackModeRef.current;
    setStreamingState('connecting');
    didConnectionTimeoutRef.current = setTimeout(() => {
      didConnectionTimeoutRef.current = null;
      console.warn('[DIDAvatar] timeout esperando la conexión dinámica.');
      setStreamingState('failed');
    }, DID_CONNECTION_TIMEOUT_MS);

    try {
      const stream = await didFetch<DidTalkStreamResponse>('/talks/streams', {
        method: 'POST',
        body: JSON.stringify({
          source_url: initialConfig.avatarImgUrl,
        }),
      });

      if (!isCurrentConnection()) {
        didFetch(`/talks/streams/${stream.id}`, {
          method: 'DELETE',
          body: JSON.stringify({ session_id: stream.session_id }),
        }).catch(() => undefined);
        return null;
      }

      const peerConnection = new RTCPeerConnection({
        iceServers: stream.ice_servers || [],
      });
      didPeerConnectionRef.current = peerConnection;
      isDynamicDidStreamRef.current = true;
      streamIdRef.current = stream.id;
      sessionIdRef.current = stream.session_id;

      peerConnection.ontrack = (event) => {
        const [mediaStream] = event.streams;
        if (!mediaStream) {
          return;
        }

        didStreamRef.current = mediaStream;
        didStreamReadyRef.current = true;
        if (talkVideoRef.current) {
          talkVideoRef.current.loop = false;
          talkVideoRef.current.muted = false;
          talkVideoRef.current.src = '';
          talkVideoRef.current.srcObject = mediaStream;
          setIsDidStreamVisible(true);
          playDidVideo(talkVideoRef.current);
        }
      };

      peerConnection.oniceconnectionstatechange = () => {
        const state = peerConnection.iceConnectionState;
        console.info('[DIDAvatar] ICE connection', state);
        if (didPeerConnectionRef.current !== peerConnection || !isCurrentConnection()) {
          return;
        }
        if (state === 'connected' || state === 'completed') {
          clearDidConnectionTimeout();
        }
        if (state === 'failed' || state === 'closed') {
          setStreamingState('failed');
        }
      };

      peerConnection.onicecandidate = (event) => {
        if (!event.candidate || !streamIdRef.current || !sessionIdRef.current) {
          return;
        }

        didFetch(`/talks/streams/${streamIdRef.current}/ice`, {
          method: 'POST',
          body: JSON.stringify({
            candidate: event.candidate.candidate,
            sdpMid: event.candidate.sdpMid,
            sdpMLineIndex: event.candidate.sdpMLineIndex,
            session_id: sessionIdRef.current,
          }),
        }).catch((error) => {
          console.warn('Error enviando ICE candidate a D-ID:', error);
        });
      };

      await peerConnection.setRemoteDescription(new RTCSessionDescription(stream.offer));
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);

      await didFetch(`/talks/streams/${stream.id}/sdp`, {
        method: 'POST',
        body: JSON.stringify({
          answer,
          session_id: stream.session_id,
        }),
      });

      if (!isCurrentConnection()) {
        await closeDynamicDidStream();
        return null;
      }

      console.info('[DIDAvatar] dynamic stream created', {
        stream_id: stream.id,
        avatarImgUrl: initialConfig.avatarImgUrl,
      });
      showDidIdleVideo('dynamic-connected');
      clearDidConnectionTimeout();
      setStreamingState(ConnectionState.Connected);
      return true;
    } catch (error) {
      if (!isCurrentConnection()) {
        return null;
      }
      clearDidConnectionTimeout();
      console.error('Error creando stream dinámico de D-ID:', error);
      setStreamingState('failed');
      return false;
    }
  };

  const speakDynamicDidStream = async (speakText: string) => {
    if (!streamIdRef.current || !sessionIdRef.current) {
      throw new Error('El stream dinámico de D-ID no está inicializado');
    }

    showDidSpeakingStream();

    await didFetch(`/talks/streams/${streamIdRef.current}`, {
      method: 'POST',
      body: JSON.stringify({
        script: {
          type: 'text',
          input: speakText,
          provider: {
            type: 'microsoft',
            voice_id: initialConfig?.azureVoiceId ?? 'es-ES-DarioNeural',
          },
        },
        config: {
          stitch: true,
        },
        session_id: sessionIdRef.current,
      }),
    });
  };

  // true: conectado · false: ha fallado · null: cancelado por otra conexión o por el modo audio.
  const connect = async (): Promise<boolean | null> => {
    console.log('Connect function invoked');
    if (shouldUseDynamicDidStreaming()) {
      return connectDynamicDidStream();
    }

    if (agentManagerRef.current && streamingState === ConnectionState.Connected) {
      console.log('Already connected, existing function');
      return true;
    }

    await disconnectAgent();
    const connectionGeneration = ++didConnectionGenerationRef.current;
    const isCurrentConnection = () =>
      connectionGeneration === didConnectionGenerationRef.current &&
      !audioFallbackModeRef.current;
    setStreamingState('connecting');
    didConnectionTimeoutRef.current = setTimeout(() => {
      didConnectionTimeoutRef.current = null;
      console.warn('[DIDAvatar] timeout esperando la conexión del agente.');
      setStreamingState('failed');
    }, DID_CONNECTION_TIMEOUT_MS);

    const agentId = initialConfig?.didAgentId || process.env.REACT_APP_DID_AGENT_ID;
    const clientKey =
      initialConfig?.didClientKey || process.env.REACT_APP_DID_CLIENT_KEY;

    if (!agentId || !clientKey) {
      clearDidConnectionTimeout();
      console.error('D-ID Agent SDK configuration is missing');
      setStreamingState('failed');
      return false;
    }

    let agentManager: AgentManager | null = null;
    try {
      agentManager = await createAgentManager(agentId, {
        auth: {
          type: 'key',
          clientKey,
        },
        callbacks: {
          onSrcObjectReady: (srcObject: MediaStream) => {
            if (!isCurrentConnection()) {
              return;
            }
            didStreamRef.current = srcObject;
            setIsDidStreamVisible(false);
            console.info('[DIDAvatar] source stream ready', {
              audioTracks: srcObject.getAudioTracks().length,
              videoTracks: srcObject.getVideoTracks().length,
            });
            if (talkVideoRef.current) {
              talkVideoRef.current.src = '';
              talkVideoRef.current.srcObject = srcObject;
            }
          },
          onStreamCreated: (stream) => {
            if (!isCurrentConnection()) {
              return;
            }
            streamIdRef.current = stream.stream_id;
            sessionIdRef.current = stream.session_id;
            console.info('[DIDAvatar] stream created', stream);
          },
          onConnectionStateChange: (state) => {
            console.info('[DIDAvatar] connection', state);
            if (conversationStoppedForFeedbackRef.current || !isCurrentConnection()) {
              return;
            }
            if (state === ConnectionState.Connected) {
              clearDidConnectionTimeout();
              didStreamReadyRef.current = true;
              if (!showDidIdleVideo('connected') && talkVideoRef.current && didStreamRef.current) {
                showDidSpeakingStream();
              }
            }
            setStreamingState(state);
          },
          onVideoStateChange: (state) => {
            console.info('[DIDAvatar] video', state, {
              ready: didStreamReadyRef.current,
              hasStream: Boolean(didStreamRef.current),
            });
            if (!isCurrentConnection() || !talkVideoRef.current) {
              return;
            }
            if (state === StreamingState.Stop) {
              if (suppressDidSpeechRef.current) {
                // Fin de una respuesta que el usuario ya pausó.
                suppressDidSpeechRef.current = false;
                showDidIdleVideo('stop-after-pause');
                return;
              }
              clearDidSpeechWatch();
              setAvatarSpeaking(false);
              if (didStreamReadyRef.current) {
                if (!showDidIdleVideo('stop') && didStreamRef.current) {
                  showDidSpeakingStream();
                }
                completeAvatarInteraction();
                changeVisualMode('finish');
              } else {
                setIsDidStreamVisible(false);
              }
            }
            if (state === StreamingState.Start) {
              didStreamReadyRef.current = true;
              if (suppressDidSpeechRef.current) {
                talkVideoRef.current.muted = true;
                return;
              }
              markDidSpeechStarted();
              if (!showDidSpeakingStream()) {
                console.warn('D-ID stream is not ready on START');
              }
            }
          },
          onError: (error, errorData) => {
            if (conversationStoppedForFeedbackRef.current || !isCurrentConnection()) {
              console.warn('D-ID Agent SDK error (ignorado, sesión cerrándose):', error);
              return;
            }
            console.error('D-ID Agent SDK error:', error, errorData);
            clearDidConnectionTimeout();
            setStreamingState('failed');
          },
        },
        enableAnalitics: false,
        streamOptions: {
          compatibilityMode: 'on',
          streamWarmup: true,
        },
      });

      if (!isCurrentConnection()) {
        await agentManager.disconnect().catch(() => undefined);
        return null;
      }

      agentManagerRef.current = agentManager;
      connectingAgentRef.current = agentManager;
      await agentManager.connect();
      connectingAgentRef.current = null;
      if (!isCurrentConnection()) {
        await agentManager.disconnect().catch(() => undefined);
        return null;
      }
      return true;
    } catch (error) {
      if (connectingAgentRef.current === agentManager) {
        connectingAgentRef.current = null;
      }
      if (!isCurrentConnection()) {
        agentManager?.disconnect().catch(() => undefined);
        return null;
      }
      clearDidConnectionTimeout();
      console.error('Error during D-ID Agent connection:', error);
      setStreamingState('failed');
      return false;
    }
  };

  const finishAudioSpeech = (player: speechsdk.SpeakerAudioDestination) => {
    if (
      speechPlayerRef.current !== player ||
      conversationStoppedForFeedbackRef.current
    ) {
      return;
    }

    clearSpeechCompletionWatchdog();
    speechPlaybackStartedRef.current = false;
    speechPlayerRef.current = null;
    const synthesizer = speechSynthesizerRef.current;
    speechSynthesizerRef.current = null;

    try {
      player.pause();
      player.mute();
      player.close?.();
      synthesizer?.close();
    } catch (error) {
      console.warn('Error cerrando la reproducción de voz:', error);
    }

    setAvatarSpeaking(false);
    completeAvatarInteraction();
    changeVisualMode('finish');
  };

  // SpeakerAudioDestination solo sirve para una reproducción: tras la primera
  // cierra su MediaSource y ya no emite onAudioStart/onAudioEnd. Por eso cada
  // respuesta usa un reproductor y un sintetizador nuevos.
  const getSynthesizer = async (enableVolume: boolean) => {
    clearSpeechCompletionWatchdog();
    releaseAudioPlayback();

    const tokenObj = await getAzureVoiceToken();
    const speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(
      tokenObj.authToken,
      tokenObj.region
    );
    speechConfig.speechRecognitionLanguage =
      initialConfig?.speechRecognitionLanguage ?? 'es-ES';
    speechConfig.speechSynthesisLanguage =
      initialConfig?.speechRecognitionLanguage ?? 'es-ES';
    speechConfig.speechSynthesisVoiceName =
      initialConfig?.azureVoiceId ?? 'es-ES-DarioNeural';
    const player = new speechsdk.SpeakerAudioDestination();
    speechPlayerRef.current = player;
    player.volume = enableVolume ? 1 : 0;
    if (enableVolume) {
      player.unmute?.();
    }
    player.onAudioStart = function (s) {
      console.log('Comienza el speech');
      if (!enableVolume) {
        player.volume = 0;
        player.mute();
      } else {
        player.volume = 1;
        player.unmute?.();
      }
      if (speechPlayerRef.current === player) {
        speechPlaybackStartedRef.current = true;
        clearSpeechCompletionWatchdog();
        speechCompletionWatchdogRef.current = setTimeout(() => {
          finishAudioSpeech(player);
        }, estimateSpeechMs(activeSpeechTextRef.current) + AUDIO_PLAYBACK_GRACE_MS);
        setAvatarSpeaking(true);
      }
    };
    player.onAudioEnd = function (s) {
      console.log('Termina el speech');
      finishAudioSpeech(player);
    };
    const synthesizer = new speechsdk.SpeechSynthesizer(
      speechConfig,
      speechsdk.AudioConfig.fromSpeakerOutput(player)
    );
    return synthesizer;
  };

  const initializeSpeech = async ({
    speakText,
    timeoutTime,
    volume,
  }: AzureTalk) => {
    if (conversationStoppedForFeedbackRef.current) {
      return;
    }

    const synthesizer = await getSynthesizer(volume);
    activeSpeechTextRef.current = speakText;
    if (conversationStoppedForFeedbackRef.current) {
      synthesizer.close();
      return;
    }
    speechSynthesizerRef.current = synthesizer;
    const handleSpeechFailure = (reason: string) => {
      const wasActive = speechSynthesizerRef.current === synthesizer;
      try {
        synthesizer.close();
      } catch {}
      if (!wasActive) {
        // Se detuvo a propósito (pausa o feedback).
        return;
      }
      console.warn('Fallo en la síntesis de voz:', reason);
      speechSynthesizerRef.current = null;
      speechPlayerRef.current = null;
      clearSpeechCompletionWatchdog();
      setAvatarSpeaking(false);
      cancelAvatarInteraction();
      if (conversationStoppedForFeedbackRef.current) {
        return;
      }
      showToast('No se pudo reproducir la voz. Puedes seguir hablando.', 'warn');
      changeVisualMode('finish');
    };
    const cb = (e: speechsdk.SpeechSynthesisResult) => {
      // Si termina bien se cierra en onAudioEnd, cuando acaba de sonar.
      if (e.reason !== speechsdk.ResultReason.SynthesizingAudioCompleted) {
        handleSpeechFailure(e.errorDetails || String(e.reason));
      }
    };
    const err = (e: string) => handleSpeechFailure(e);
    speechTimeoutRef.current = setTimeout(() => {
      speechTimeoutRef.current = null;
      if (conversationStoppedForFeedbackRef.current) {
        synthesizer.close();
        if (speechSynthesizerRef.current === synthesizer) {
          speechSynthesizerRef.current = null;
        }
        return;
      }
      synthesizer.speakTextAsync(speakText, cb, err);
      clearSpeechCompletionWatchdog();
      const player = speechPlayerRef.current;
      if (!player) {
        handleSpeechFailure('No se creó el reproductor de audio');
        return;
      }
      speechCompletionWatchdogRef.current = setTimeout(() => {
        speechCompletionWatchdogRef.current = null;
        if (
          speechPlayerRef.current !== player ||
          conversationStoppedForFeedbackRef.current ||
          speechPlaybackStartedRef.current
        ) {
          return;
        }
        console.warn('La voz no inició la reproducción; se da por terminada.');
        finishAudioSpeech(player);
      }, AUDIO_SPEECH_WATCHDOG_EXTRA_MS + 15000);
    }, timeoutTime);
  };

  const talk = async (
    userMessage: string,
    initialGreeting: boolean = false
  ) => {
    if (conversationStoppedForFeedbackRef.current) {
      setIsChatSent(false);
      setIsBotThinking(false);
      showToast('La conversación está pausada tras solicitar feedback.', 'warn');
      return;
    }

    if (isWaitingResponse) {
      showToast('Se está procesando una respuesta, espera un momento.', 'warn');
      // isMicEnabled !== undefined && changeVisualMode('open-mic');
      return;
    }
    if (
      (streamingMode || streamingOnlyMode) &&
      shouldUseDidStreaming() &&
      !audioFallbackModeRef.current &&
      streamingState !== ConnectionState.Connected
    ) {
      showToast('La conexión con el streaming no es estable.', 'warn');
      console.log('Talk - la conexión no es estable o no está conectada');
      return;
    }

    const interactionSession = interactionLimitSessionRef.current;
    if (!interactionSession || !interactionSession.canStart()) {
      if (interactionSession?.reachedLimit || interactionLimitReached) {
        showToast('Se ha alcanzado el límite de la sesión.', 'warn');
      } else {
        showToast('Se está procesando una respuesta, espera un momento.', 'warn');
      }
      return;
    }

    if (!interactionSession.start()) {
      showToast('Se está procesando una respuesta, espera un momento.', 'warn');
      return;
    }

    try {
      const talkRequestId = ++activeTalkRequestRef.current;
      !initialGreeting &&
        addNewMessage(toMessageFormat(userMessage), 'user');
      isWaitingResponse = true;
      if (!streamingMode && !streamingOnlyMode) {
        setChatInput('');
        setIsChatSent(true);
      }
      setIsBotThinking(true);
      console.log('Recibiendo una respuesta del Avatar...');
      let chatText: string | undefined;
      let gptResponse: AvatarResponse | undefined;
      if (avatarType?.toLowerCase() === 'gptceu') {

        let matchedTag: string | null = null;
        for (const tag in tagFrases) {
          if (tagFrases[tag].includes(userMessage)) {
            matchedTag = tag;
            break;
          }
        }

        // Si el mensaje viene de un tag, se envía su ID de proceso como parámetro "dominio"
        let idProceso: string | null = null;
        if (matchedTag) {
          try {
            idProceso = await getIdProcesoByTag(matchedTag, environment);
          } catch (error) {
            console.warn('No se pudo obtener el ID de proceso del tag:', error);
          }
          if (!idProceso) {
            console.warn(`Tag "${matchedTag}" sin ID de proceso en el CSV de ${environment}.`);
          }
        }

        gptResponse = await getGptData(
          userMessage,
          userToken,
          user,
          idProceso ? null : matchedTag,
          environment,
          idProceso ?? getUrlDominio()
        );
      } else {
        chatText = await getVariableData(userMessage, getConversationSessionId());
      }

      if (
        conversationStoppedForFeedbackRef.current ||
        talkRequestId !== activeTalkRequestRef.current
      ) {
        isWaitingResponse = false;
        cancelAvatarInteraction();
        setIsBotThinking(false);
        setIsChatSent(false);
        return;
      }

      if (!gptResponse && !chatText) {
        throw new Error('No se ha recibido respuesta del backend. Comprueba tu conexión.');
      }

      const shouldPrioritizeExam = gptResponse
        ? isTutorPersonalizadoDomain(gptResponse.dominio)
        : false;

      const interactiveExam = gptResponse
        ? parseAvatarExamPayload(gptResponse.respuesta_usuario)
        : null;

      if (gptResponse) {
        if (shouldPrioritizeExam) {
          setRespuestaJson(null);
          setIsDashboard(false);
          setExamsOnly(null);
          setUniversityFunnelsData(null);
          setTitulacionBarsData(null);
          setOverviewBarsData(null);
          setRankingTableData(null);
          setPersonCardsData(null);
          setDepartmentOrgChartData(null);
          setChartLabels([]);
          setChartData([]);
          setChartTitle('');
          settipoGrafica(undefined);
          setTableData(null);
          setIsTemplate('');
          setShowChart(false);
        }

        if (shouldPrioritizeExam && interactiveExam) {
          gptResponse = {
            ...gptResponse,
            respuesta_usuario: buildExamIntroMessage(interactiveExam),
            formato_respuesta_usuario: 'Exam',
            respuesta_json: sanitizeAvatarExamPayload(interactiveExam),
          };
        }

        const hasChartData = !shouldPrioritizeExam && !interactiveExam
          ? processChartDataResponse(gptResponse)
          : false;

        // Nueva lógica: Detecta si hay respuesta_json con dashboard o solo exámenes
        // Checamos si respuesta_json existe (puede ser string o objeto)
        if (!hasChartData && !shouldPrioritizeExam && !interactiveExam && gptResponse?.respuesta_json) {
          // Intentamos parsear si es string
          let parsedJson: DashboardData | null = null;
          if (typeof gptResponse.respuesta_json === 'string') {
            try {
              parsedJson = JSON.parse(gptResponse.respuesta_json);
            } catch (e) {
              console.error('Error parseando respuesta_json:', e);
            }
          } else {
            parsedJson = gptResponse.respuesta_json as DashboardData;
          }
          
          const hasTareasTotales =
            parsedJson && (parsedJson as any).tareas_totales !== undefined;
          const hasExamenesArray =
            parsedJson &&
            Array.isArray((parsedJson as any).examenes) &&
            (parsedJson as any).examenes.length > 0;

          // Caso 1: JSON solo de exámenes (como el ejemplo que has pasado)
          if (parsedJson && hasExamenesArray && !hasTareasTotales) {
            setRespuestaJson(null);
            setIsDashboard(false);
            setExamsOnly((parsedJson as any).examenes);
            setShowChart(true);

            gptResponse = toMessageFormat(gptResponse || chatText);
            isWaitingResponse = false;
            setIsBotThinking(false);
            addNewMessage(gptResponse, 'bot', initialConfig.avatarName || 'GPT-CEU');
            completeAvatarInteraction();
            if (!streamingMode && !streamingOnlyMode) {
              setIsChatSent(false);
            }
            changeVisualMode('finish');
            return;
          }

          // Caso 2: Dashboard completo (tareas_totales presente)
          if (parsedJson && hasTareasTotales) {
            setExamsOnly(null);
            setRespuestaJson(parsedJson);
            setIsDashboard(true);
            setShowChart(true);

            // Retorna aquí sin procesar más para evitar que processGptResponse lo modifique
            gptResponse = toMessageFormat(gptResponse || chatText);
            isWaitingResponse = false;
            setIsBotThinking(false);
            addNewMessage(gptResponse, 'bot', initialConfig.avatarName || 'GPT-CEU');
            completeAvatarInteraction();
            if (!streamingMode && !streamingOnlyMode) {
              setIsChatSent(false);
            }
            changeVisualMode('finish');
            return;
          }
        }
        
        // Si no es dashboard ni exámenes, procesar normalmente para gráficos/plantillas
        if (!hasChartData && !shouldPrioritizeExam && !interactiveExam) {
          processGptResponse(
            gptResponse,
            setChartLabels,
            setChartData,
            setNumConsultas,
            setChartTitle,
            settipoGrafica,
            setTableData,
            setIsTemplate
          );
          // Al mostrar gráficos normales, ocultamos vistas de dashboard
          setRespuestaJson(null);
          setIsDashboard(false);
          setExamsOnly(null);
        }
      }

      gptResponse = toMessageFormat(gptResponse || chatText);

      // Si es HTML de simulaimnesis (sala de espera), mostrar en chat sin intentar hablar
      if (
        typeof gptResponse.respuesta_usuario === 'string' &&
        gptResponse.dominio?.includes('Simulaimnesis') &&
        (gptResponse.respuesta_usuario.includes('class="sala-espera"') ||
         gptResponse.formato_respuesta_usuario === 'HTML')
      ) {
        isWaitingResponse = false;
        setIsBotThinking(false);
        addNewMessage(gptResponse, 'bot', initialConfig.avatarName || 'GPT-CEU');
        completeAvatarInteraction();
        if (!streamingMode && !streamingOnlyMode) {
          setIsChatSent(false);
        }
        changeVisualMode('finish');
        return;
      }

      const shouldSpeakResponse =
        streamingMode ||
        streamingOnlyMode ||
        (voiceSpeechMode && isMicEnabled !== undefined);
      let speakText = '';

      if (shouldSpeakResponse) {
        const rawShortResponse: unknown = gptResponse.respuesta_corta;
        const shortResponse =
          typeof rawShortResponse === 'string'
            ? rawShortResponse.trim()
            : '';

        if (shortResponse) {
          speakText = shortResponse;
        } else {
          const userResponse = gptResponse.respuesta_usuario ?? '';
          const isHtmlResponse =
            gptResponse.formato_respuesta_usuario?.toLowerCase() === 'html' ||
            userResponse.trim().startsWith('<');

          if (isHtmlResponse) {
            const container = document.createElement('div');
            container.innerHTML = userResponse;
            speakText = (container.textContent ?? '').replace(/\s+/g, ' ').trim();
          } else {
            speakText = userResponse.trim();
          }
        }
      }

      if (shouldSpeakResponse && !speakText.trim()) {
        throw new Error('El backend no ha devuelto una respuesta para reproducir.');
      }

      const speakTalk: AzureTalk = {
        speakText,
        volume: VOLUMEN_ON,
        timeoutTime: 0,
      };
      isWaitingResponse = false;
      setIsBotThinking(false);
      addNewMessage(gptResponse, 'bot', initialConfig.avatarName || 'GPT-CEU');
      if (
        conversationStoppedForFeedbackRef.current ||
        talkRequestId !== activeTalkRequestRef.current
      ) {
        return;
      }

      if (!shouldSpeakResponse || !speakTalk.speakText) {
        setIsChatSent(false);
        completeAvatarInteraction();
        changeVisualMode('finish');
        return;
      }

      if (userPausedRef.current) {
        // La respuesta se reproduce al reanudar la conversación.
        heldSpeakTalkRef.current = speakTalk;
        setIsChatSent(false);
        setDisplayText('En pausa');
        return;
      }

      await deliverSpeech(speakTalk);
    } catch (e) {
      console.error('Fallo en Talk:', e);
      cancelAvatarInteraction();
      isWaitingResponse = false;
      setIsBotThinking(false);
      setIsChatSent(false);
      setAvatarSpeaking(false);
      setDisplayText('No se pudo responder. Vuelve a intentarlo.');
      showToast('No se pudo obtener respuesta del avatar. Inténtalo de nuevo.', 'error');
      if (
        (streamingMode || streamingOnlyMode || isMicEnabled !== undefined) &&
        !conversationStoppedForFeedbackRef.current
      ) {
        changeVisualMode('finish');
      }
    }
  };

  const shouldSpeakWithDid = (): boolean =>
    (streamingMode || streamingOnlyMode) &&
    shouldUseDidStreaming() &&
    !audioFallbackModeRef.current;

  // Reproduce una respuesta con el vídeo de D-ID o, si no está disponible o
  // falla, solo con la voz de Azure sin perder el turno.
  const deliverSpeech = async (speakTalk: AzureTalk) => {
    if (!shouldSpeakWithDid()) {
      setIsChatSent(false);
      try {
        await initializeSpeech(speakTalk);
      } catch (error) {
        console.error('No se pudo reproducir la respuesta por voz:', error);
        setAvatarSpeaking(false);
        cancelAvatarInteraction();
        showToast('No se pudo reproducir la voz. Puedes seguir hablando.', 'warn');
        changeVisualMode('finish');
      }
      return;
    }

    console.log('Intentando inicializar el streaming...');
    try {
      if (shouldUseDynamicDidStreaming()) {
        console.info('[DIDAvatar] dynamic speak', {
          textLength: speakTalk.speakText.length,
          voiceId: initialConfig?.azureVoiceId ?? 'es-ES-DarioNeural',
        });
        startDidSpeechWatch(speakTalk, true);
        await speakDynamicDidStream(speakTalk.speakText);
        scheduleDynamicDidFinish(speakTalk.speakText);
      } else if (!agentManagerRef.current) {
        throw new Error('D-ID Agent Manager no está inicializado');
      } else {
        console.info('[DIDAvatar] speak', {
          textLength: speakTalk.speakText.length,
          voiceId: initialConfig?.azureVoiceId ?? 'es-ES-DarioNeural',
        });
        startDidSpeechWatch(speakTalk, false);
        await agentManagerRef.current.speak({
          type: 'text',
          provider: {
            type: Providers.Microsoft,
            voice_id: initialConfig?.azureVoiceId ?? 'es-ES-DarioNeural',
          },
          input: speakTalk.speakText,
        });
      }
      console.log('Talk completed');
    } catch (error) {
      console.error('Fallo con el streaming:', error);
      if (conversationStoppedForFeedbackRef.current || audioFallbackModeRef.current) {
        return;
      }
      // Si el usuario la pausó mientras tanto, no se repite.
      const isStillPending = pendingSpeakTalkRef.current === speakTalk;
      activateAudioFallback({
        reason: 'did-speak-error',
        autoStartMic: !isStillPending,
        fallbackSpeakTalk: isStillPending ? speakTalk : undefined,
      });
    }
  };
  // #############################
  // Gestión del render del video y streaming

  const extractDivContent = (rawResponse: string): string | null => {
    const divContentMatch = rawResponse.match(/<div>(.*)<\/div>/s);
    return divContentMatch?.[1] || null;
  };

  const decodeHtmlEntities = (input: string) => {
    const entities: Record<string, string> = {
      '&quot;': '"',
      '&apos;': "'",
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&nbsp;': ' ',
    };

    return input.replace(/&[a-z]+;/gi, (match) => entities[match] ?? match);
  };

  const stripHtmlTags = (input: string) => input.replace(/<[^>]*>/g, ' ');

  const parseRawDataString = (rawDataString: string): any[] => {
    try {
      const firstBracket = rawDataString.indexOf('[');
      const lastBracket = rawDataString.lastIndexOf(']');
      
      let stringToParse = rawDataString;
      if (firstBracket !== -1 && lastBracket !== -1 && lastBracket > firstBracket) {
          stringToParse = rawDataString.substring(firstBracket, lastBracket + 1);
      }

      let cleanedString = decodeHtmlEntities(stringToParse)
        // eslint-disable-next-line no-control-regex
        .replace(/\x1b\[[0-9;]*m/g, '') 
        .replace(/\n/g, ' ')
        .replace(/\r/g, ' ');

      cleanedString = stripHtmlTags(cleanedString);

      cleanedString = cleanedString
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false')
        .replace(/\bNone\b/g, 'null');

      // eslint-disable-next-line no-new-func
      const parsedData = new Function(`return ${cleanedString};`)();

      return Array.isArray(parsedData) ? parsedData : [];
    } catch (err) {
      //console.error("Error al parsear los datos:", err);
      return [];
    }
  };

  const processParsedArray = (parsedArray) => {
    const labelsArray = [];
    const dataArray = [];

    parsedArray.forEach((item) => {
      try {
        let parsedRespuesta;

        if (typeof item.respuesta_corta === "string") {
          try {
            parsedRespuesta = JSON.parse(item.respuesta_corta);
            if (
              parsedRespuesta &&
              typeof parsedRespuesta === "object" &&
              typeof parsedRespuesta.respuesta_corta === "string"
            ) {
              parsedRespuesta = JSON.parse(parsedRespuesta.respuesta_corta);
            }
          } catch (err) {
            console.error("Error haciendo doble parseo de respuesta_corta:", err);
            parsedRespuesta = null;
          }
        } else {
          parsedRespuesta = item.respuesta_corta;
        }

        let labels = [];
        let values = [];

        if (Array.isArray(parsedRespuesta)) {
          if (
            parsedRespuesta.length > 0 &&
            typeof parsedRespuesta[0] === "object"
          ) {
            const firstEntry = parsedRespuesta[0];

            const yearKey = [
              "any_academico",
              "curso",
              "curso_academico",
            ].find((k) => firstEntry.hasOwnProperty(k));

            const numericKeys = Object.keys(firstEntry).filter(
              (k) => typeof firstEntry[k] === "number" && k !== yearKey
            );

            if (yearKey && numericKeys.length === 1) {
              const valueKey = numericKeys[0];
              parsedRespuesta.forEach((entry) => {
                const label = String(entry[yearKey]);
                const value = entry[valueKey];
                labels.push(label);
                values.push(value);
              });
            } else if (numericKeys.length > 1) {
              labels = numericKeys.map((k) =>
                k
                  .replace(/total_/gi, "")
                  .replace(/_/g, " ")
                  .replace(/\b(ingreso|reingreso)\b/gi, (m) =>
                    m === "ingreso" ? "Nuevo Ingreso" : "Reingreso"
                  )
              );

              values = numericKeys.map((k) =>
                parsedRespuesta.reduce((sum, entry) => sum + (entry[k] || 0), 0)
              );
            } else {
              const textKey = Object.keys(firstEntry).find(
                (key) => typeof firstEntry[key] === "string"
              );
              const numberKey = Object.keys(firstEntry).find(
                (key) => typeof firstEntry[key] === "number"
              );
              if (textKey && numberKey) {
                parsedRespuesta.forEach((entry) => {
                  const label = entry[textKey];
                  const value = entry[numberKey];
                  if (label && typeof value === "number") {
                    labels.push(label);
                    values.push(value);
                  }
                });
              }
            }
          }
        } else if (
          typeof parsedRespuesta === "object" &&
          parsedRespuesta !== null
        ) {
          const numberKey = Object.keys(parsedRespuesta).find(
            (key) => typeof parsedRespuesta[key] === "number"
          );
          if (numberKey) {
            const label = item.consulta_resumida || "Resultado";
            const value = parsedRespuesta[numberKey];
            labels.push(label);
            values.push(value);
          }
        } else {
          labels.push(item.consulta_resumida || "Resultado");
          values.push(parseFloat(item.respuesta) || 0);
        }

        if (labels.length > 0 && values.length > 0) {
          labelsArray.push(labels);
          dataArray.push(values);
        }
      } catch (e) {
        console.error("Error procesando item:", e);
      }
    });

    if (
      parsedArray.length > 1 &&
      labelsArray.every((labels) => labels.length === 1)
    ) {
      const combinedLabels = labelsArray.flat();
      const combinedValues = dataArray.flat();
      return {
        labelsArray: [combinedLabels],
        dataArray: [combinedValues],
        chartType: "combined",
      };
    } else {
      return {
        labelsArray,
        dataArray,
        chartType: parsedArray.length > 1 ? "multiple" : "single",
      };
    }
  };

  const processGptResponse = (
    gptResponse: any,
    setChartLabels: Function,
    setChartData: Function,
    setNumConsultas: Function,
    setChartTitle: Function,
    settipoGrafica: Function,
    setTableData: Function,
    setIsTemplate: Function,
  ) => {
    try {
      const rawResponse = gptResponse.respuesta_usuario;

      if (typeof rawResponse !== 'string') {
        console.log('respuesta_usuario no es un string.');
        return;
      }

      const rawDataString = extractDivContent(rawResponse);
      if (!rawDataString) {
        console.log('No se encontró contenido dentro del div. Intentando procesar la respuesta completa.');
        if (rawResponse.trim().length === 0) {
          return;
        }
      }
      const parsedArray = parseRawDataString(rawDataString || rawResponse);
      //console.log('Parsed Array:', parsedArray);
      setNumConsultas(parsedArray.length);

      let labelsArray = [];
      let dataArray = [];
      let titlesArray = [];
      let tiposGraficaArray = [];

      function safeParseGlobalRemoved(input: any) {
        if (typeof input === 'string') {
          try {
            return JSON.parse(input);
          } catch {
            return input;
          }
        }
        return input;
      }

      // **Caso especial: Gráficos comparativos agrupados centro**
      const isGroupedTasas =
        parsedArray.length > 1 &&
        parsedArray.every(
          item =>
            item.template === "Tasas" &&
            (item.tipo_grafico === "Barra" || item.tipo_grafico === "Barras") &&
            typeof item.titulo_grafico === "string" &&
            item.titulo_grafico.toLowerCase().includes("centro")
        );

      if (isGroupedTasas) {
        const safeParse = (input: any) => {
          if (typeof input === 'string') {
            try {
              return JSON.parse(input);
            } catch {
              return input;
            }
          }
          return input;
        };
        let allData: any[] = [];
        parsedArray.forEach(item => {
          try {
            const arr = Array.isArray(safeParse(item.respuesta_corta)) ? safeParse(item.respuesta_corta) : [];
            allData = allData.concat(arr);
          } catch { }
        });

        const years = [...new Set(allData.map(d => d.any_academico))];
        const centerKey =
          allData[0].centro
            ? "centro"
            : allData[0].nombre_centro
              ? "nombre_centro"
              : null;
        if (!centerKey) {
          // fallback a agrupado normal
          // ...existing code...
        }
        const centers = [...new Set(allData.map(d => d[centerKey]))];

        let tasaExitoOGraduacion = null;
        if (allData.some(d => d.tasa_exito !== undefined)) {
          tasaExitoOGraduacion = "tasa_exito";
        } else if (allData.some(d => d.tasa_graduacion !== undefined)) {
          tasaExitoOGraduacion = "tasa_graduacion";
        }

        const metricKeys = [
          "tasa_rendimiento",
          tasaExitoOGraduacion,
          "tasa_eficiencia"
        ].filter(Boolean);

        const chartColors = getChartColors(centers.length);
        const datasets = metricKeys
          .map((metric, idx) => {
            if (!allData.some(d => d[metric] !== undefined)) return null;
            return {
              label:
                metric === "tasa_rendimiento"
                  ? "Tasa de Rendimiento"
                  : metric === "tasa_exito"
                    ? "Tasa de Éxito"
                    : metric === "tasa_graduacion"
                      ? "Tasa de Graduación"
                      : "Tasa de Eficiencia",
              data: centers.map(center =>
                years.map(year => {
                  const entry = allData.find(
                    d =>
                      d.any_academico === year &&
                      d[centerKey] === center &&
                      d[metric] !== undefined
                  );
                  return entry ? entry[metric] : 0;
                })
              ),
              backgroundColor: chartColors[idx % chartColors.length],
              centers,
              chartTitle:
                (parsedArray.find(item =>
                  item.respuesta_corta.includes(metric)
                )?.titulo_grafico) ||
                (parsedArray[idx]?.titulo_grafico || "")
            };
          })
          .filter(Boolean);

        const chartDataForGrouped = datasets.map(ds => ({
          labels: years,
          datasets: ds.centers.map((center, i) => ({
            label: center,
            data: years.map((year, j) => ds.data[i][j]),
            backgroundColor: chartColors[i % chartColors.length],
          })),
          title: ds.chartTitle,
        }));

        setChartLabels(chartDataForGrouped.map(c => c.labels));
        setChartData(chartDataForGrouped.map(c => c.datasets));
        setChartTitle(chartDataForGrouped.map(c => c.title));
        settipoGrafica(Array(chartDataForGrouped.length).fill("GroupedBar"));
        setIsTemplate(parsedArray[0]?.template || false);
        return;
      }

      // barras agrupadas por titulación y año académico
      const isGroupedByTitulacionAndYear =
        parsedArray.length > 1 &&
        parsedArray.every(
          item =>
            item.template === "Tasas" &&
            (item.tipo_grafico === "Barra" || item.tipo_grafico === "Barras") &&
            typeof item.titulo_grafico === "string" &&
            (
              item.titulo_grafico.toLowerCase().includes("titulacion") ||
              item.titulo_grafico.toLowerCase().includes("titulación")
            )
        );

      if (isGroupedByTitulacionAndYear) {
        let allData: any[] = [];
        parsedArray.forEach(item => {
          try {
            const arr = Array.isArray(safeParseGlobalRemoved(item.respuesta_corta)) ? safeParseGlobalRemoved(item.respuesta_corta) : [];
            allData = allData.concat(arr);
          } catch { }
        });

        let years = [...new Set(allData.map(d => d.any_academico))];
        years = years.sort();
        years = years.slice(-2);

        const titulaciones = [...new Set(allData.map(d => d.titulacion))];

        // Determinar las métricas presentes (tasa_rendimiento, tasa_graduacion, tasa_eficiencia)
        const metricKeys = ["tasa_rendimiento", "tasa_exito", "tasa_graduacion", "tasa_eficiencia"]
          .filter(metric => allData.some(d => d[metric] !== undefined && d[metric] !== null));
        // Para cada métrica, crear un dataset agrupado por titulación
        const chartColors = getChartColors(titulaciones.length);
        const datasets = metricKeys.map((metric, idx) => ({
          label:
            metric === "tasa_rendimiento"
              ? "Tasa de Rendimiento"
              : metric === "tasa_graduacion"
                ? "Tasa de Graduación"
                : "Tasa de Eficiencia",
          data: titulaciones.map(titulacion =>
            years.map(year => {
              const entry = allData.find(
                d =>
                  d.any_academico === year &&
                  d.titulacion === titulacion &&
                  d[metric] !== undefined &&
                  d[metric] !== null
              );
              return entry ? entry[metric] : 0;
            })
          ),
          titulaciones,
          backgroundColor: chartColors[idx % chartColors.length],
          chartTitle:
            parsedArray.find(item =>
              item.respuesta_corta.includes(metric)
            )?.titulo_grafico || parsedArray[idx]?.titulo_grafico || ""
        }));

        // Formato para el gráfico de barras agrupadas
        const chartDataForGrouped = datasets.map(ds => ({
          labels: years,
          datasets: ds.titulaciones.map((titulacion, i) => ({
            label: titulacion,
            data: years.map((year, j) => ds.data[i][j]),
            backgroundColor: chartColors[i % chartColors.length],
          })),
          title: ds.chartTitle,
        }));

        setChartLabels(chartDataForGrouped.map(c => c.labels));
        setChartData(chartDataForGrouped.map(c => c.datasets));
        setChartTitle(chartDataForGrouped.map(c => c.title));
        settipoGrafica(Array(chartDataForGrouped.length).fill("GroupedBar"));
        setIsTemplate(parsedArray[0]?.template || false);
        return;
      }

      // NUEVO: Caso especial para barras agrupadas por asignatura y año académico
      const isGroupedByAsignaturaAndYear =
        parsedArray.length > 1 &&
        parsedArray.every(
          item =>
            item.template === "Tasas" &&
            (item.tipo_grafico === "Barra" || item.tipo_grafico === "Barras") &&
            typeof item.titulo_grafico === "string" &&
            (
              item.titulo_grafico.toLowerCase().includes("asignatura") ||
              item.titulo_grafico.toLowerCase().includes("asignaturas")
            )
        );

      if (isGroupedByAsignaturaAndYear) {
        // Unir todos los datos de respuesta_corta
        let allData: any[] = [];
        parsedArray.forEach(item => {
          try {
            const arr = Array.isArray(safeParseGlobalRemoved(item.respuesta_corta)) ? safeParseGlobalRemoved(item.respuesta_corta) : [];
            allData = allData.concat(arr);
          } catch { }
        });

        // CORRECCIÓN AQUÍ: Usar 'any_academico' O 'curso'
        let years = [...new Set(allData.map(d => d.any_academico ?? d.curso))];
        
        // Ordenar correctamente (si son números o strings numéricos)
        years = years.sort((a, b) => (parseInt(a) || 0) - (parseInt(b) || 0));
        
        // Opcional: si hay muchos cursos/años, recortar (puedes comentar esto si quieres ver todos)
        // years = years.slice(-2);

        const asignaturas = [...new Set(allData.map(d => d.asignatura))];

        // Determinar las métricas presentes
        const metricKeys = ["tasa_rendimiento", "tasa_exito", "tasa_graduacion", "tasa_eficiencia"]
          .filter(metric => allData.some(d => d[metric] !== undefined && d[metric] !== null));
        const chartColors = getChartColors(asignaturas.length);
        // Para cada métrica, crear un dataset agrupado por asignatura
        const datasets = metricKeys.map((metric, idx) => ({
          label:
            metric === "tasa_rendimiento"
              ? "Tasa de Rendimiento"
              : metric === "tasa_graduacion"
                ? "Tasa de Graduación"
                : metric === "tasa_exito"    // Faltaba añadir etiqueta para Éxito
                ? "Tasa de Éxito" 
                : "Tasa de Eficiencia",
          data: asignaturas.map(asignatura =>
            years.map(year => {
              // CORRECCIÓN AQUÍ: Buscar coincidencia por any_academico O curso
              const entry = allData.find(
                d =>
                  (d.any_academico ?? d.curso) === year &&
                  d.asignatura === asignatura &&
                  d[metric] !== undefined &&
                  d[metric] !== null
              );
              return entry ? entry[metric] : 0;
            })
          ),
          asignaturas,
          backgroundColor: chartColors[idx % chartColors.length],
          chartTitle:
            parsedArray.find(item =>
              item.respuesta_corta.includes(metric)
            )?.titulo_grafico || parsedArray[idx]?.titulo_grafico || ""
        }));

        // Formato para el gráfico de barras agrupadas
        const chartDataForGrouped = datasets.map(ds => ({
          labels: years, // Esto ahora contendrá [1, 4] según tus datos
          datasets: ds.asignaturas.map((asignatura, i) => ({
            label: asignatura,
            data: years.map((year, j) => ds.data[i][j]),
            backgroundColor: chartColors[i % chartColors.length],
          })),
          title: ds.chartTitle,
        }));

        setChartLabels(chartDataForGrouped.map(c => c.labels));
        setChartData(chartDataForGrouped.map(c => c.datasets));
        setChartTitle(chartDataForGrouped.map(c => c.title));
        settipoGrafica(Array(chartDataForGrouped.length).fill("GroupedBar"));
        setIsTemplate(parsedArray[0]?.template || false);
        return;
      }
      if (parsedArray.every(item => item.grafico_conjunto === true)) {
        parsedArray.forEach((item, index) => {
          const data = JSON.parse(item.respuesta_corta);
          let labels;
          let datasets;

          if (data[0].hasOwnProperty('asignatura')) {
            labels = [...new Set(data.map(d => d.any_academico))];
            const asignaturas = [...new Set(data.map(d => d.asignatura))];
            const chartColors = getChartColors(asignaturas.length);

            datasets = asignaturas.map((asignatura, idx) => ({
              label: asignatura,
              data: labels.map(any => {
                const entry = data.find(d => d.any_academico === any && d.asignatura === asignatura);
                return entry ? entry.tasa_eficiencia : 0;
              }),
              backgroundColor: chartColors[idx % chartColors.length],
            }));
          } else {
            labels = data.map(d => d.any_academico);
            const chartColors = getChartColors(labels.length);
            datasets = Object.keys(data[0])
              .filter(key => key !== 'any_academico')
              .map((key, idx) => ({
                label: key.replace(/_/g, ' ').replace('tasa', 'Tasa'),
                data: data.map(d => d[key]),
                backgroundColor: chartColors[idx % chartColors.length],
              }));
          }
          labelsArray.push(labels);
          dataArray.push(datasets);
          titlesArray.push(item.titulo_grafico || "Gráfica comparativa");
          tiposGraficaArray.push('GroupedBar');
        });

        setChartLabels(labelsArray);
        setChartData(dataArray);
        setChartTitle(titlesArray);
        settipoGrafica(tiposGraficaArray);
        setIsTemplate(parsedArray[0]?.template || false);
      } else {
        const { labelsArray: processedLabels, dataArray: processedData, chartType } = processParsedArray(parsedArray);
        labelsArray = processedLabels;
        dataArray = processedData;

        let chartTitle = "Gráfica de respuesta";
        const tipoGrafica = parsedArray[0]?.tipo_grafico || "none";
        //console.log('Tipo de gráfica:', tipoGrafica);
        settipoGrafica(tipoGrafica);
        const templateType = parsedArray[0]?.template;
        setIsTemplate(templateType);

        if (tipoGrafica === 'Tabla') {
          const parsedData = JSON.parse(parsedArray[0].respuesta_corta);
          if (parsedData.length > 0) {
            const headers = Object.keys(parsedData[0]);
            const rows = parsedData;
            console.log('Tabla de datos:', { headers, rows });
            setTableData({ headers, rows });
            setChartTitle(parsedArray[0]?.titulo_grafico || "Tabla de datos");
            setShowChart(true);
          }
          return;
        }

        const safeParse = (input: any) => {
          if (typeof input === 'string') {
            try {
              return JSON.parse(input);
            } catch {
              return input;
            }
          }
          return input;
        };

        const campusItem = parsedArray.find(item =>
          item.consulta_resumida?.toLowerCase().includes('campus')
        );
        if (campusItem) {
          const campusData: any[] = Array.isArray(safeParse(campusItem.respuesta_corta))
            ? safeParse(campusItem.respuesta_corta)
            : [];
          const evolutions = {};
          campusData.forEach((row) => {
            const campus = row.campus;
            const year = row.curso || row.any_academico;
            const value = row.total_matriculados;
            if (!evolutions[campus]) {
              evolutions[campus] = { years: [], values: [] };
            }
            evolutions[campus].years.push(year);
            evolutions[campus].values.push(value);
          });
          setCampusEvolutions(evolutions);
        }

        const palabrasNuevoIngreso = ['nuevo ingreso', 'total_nuevo_ingreso', 'total_nuevos_ingreso', 'total_new_students', 'total_estudiantes', 'total_nuevos'];
        const nuevoIngresoItem = parsedArray.find(item => {
          const data = safeParse(item.respuesta_corta);
          return Array.isArray(data) && data.some(obj =>
            Object.keys(obj).some(key =>
              palabrasNuevoIngreso.some(palabra => key.toLowerCase().includes(palabra.replace(/ /g, "_").toLowerCase()))
            )
          );
        });
        if (nuevoIngresoItem) {
          const data = safeParse(nuevoIngresoItem.respuesta_corta) as any[];
          const years = data.map(d => d.curso || d.any_academico);
          const values = data.map(d => d.total_nuevo_ingreso || d.valor || 0);
          setNuevoIngresoEvolutions({ years, values });
        }

        const palabrasReingreso = ['reingreso', 're ingreso', 'total_reingreso'];
        const reingresoItem = parsedArray.find(item => {
          const data = safeParse(item.respuesta_corta);
          return Array.isArray(data) && data.some(obj =>
            Object.keys(obj).some(key =>
              palabrasReingreso.some(palabra => key.toLowerCase().includes(palabra.replace(/ /g, "_").toLowerCase()))
            )
          );
        });
        if (reingresoItem) {
          const data = safeParse(reingresoItem.respuesta_corta) as any[];
          const years = data.map(d => d.curso || d.any_academico);
          const values = data.map(d => d.total_reingreso || d.valor || 0);
          setReingresoEvolutions({ years, values });
        }

        if (nuevoIngresoItem && reingresoItem) {
          const nuevoIngresoData = safeParse(nuevoIngresoItem.respuesta_corta) as any[];
          const reingresoData = safeParse(reingresoItem.respuesta_corta) as any[];

          const nuevoIngresoValues = nuevoIngresoData.map(item =>
            item.total_nuevo_ingreso || item.Total_New_Students || item.total_nuevos_ingreso || item.total_estudiantes || item.valor || 0
          );
          const reingresoValues = reingresoData.map(item =>
            item.total_reingreso || item.Total_Reingreso || item.valor || 0
          );

          const totalNuevoIngreso = nuevoIngresoValues.reduce((acc, val) => acc + (val || 0), 0);
          const totalReingreso = reingresoValues.reduce((acc, val) => acc + (val || 0), 0);

          labelsArray[1] = ['Nuevo Ingreso', 'Reingreso'];
          dataArray[1] = [totalNuevoIngreso, totalReingreso];
          titlesArray = parsedArray.map(item => item.titulo_grafico || item.consulta_resumida || chartTitle);
          titlesArray[1] = "Comparación total de estudiantes de nuevo ingreso y reingreso";
          tiposGraficaArray = parsedArray.map(item => item.tipo_grafico || "none");
          tiposGraficaArray[1] = "Barras";

          setChartLabels(labelsArray);
          setChartData(dataArray);
          setChartTitle(titlesArray);
          settipoGrafica(tiposGraficaArray);

          if (!selectedCampus) {
            originalChartLabels.current = labelsArray;
            originalChartData.current = dataArray;
            originalChartTitle.current = titlesArray;
          }
          setOriginalMaxY(Math.max(...(dataArray[0] || [0])));
        }

        const palabrasNacionalYinternacional = ['internacionales', 'internacional', 'total_internacional', 'total_internacionales', 'nacionales', 'nacional', 'total_nacional', 'total_nacionales'];
        const NacionalesYinternacionales = parsedArray.find(item => {
          const data = safeParse(item.respuesta_corta);
          return Array.isArray(data) && data.some(obj =>
            Object.keys(obj).some(key =>
              palabrasNacionalYinternacional.some(palabra => key.toLowerCase().includes(palabra.replace(/ /g, "_").toLowerCase()))
            )
          );
        });
        console.log('NacionalesYinternacionales:', NacionalesYinternacionales);
        if (NacionalesYinternacionales) {
          const nacionalesYinternacionalesData = Array.isArray(safeParse(NacionalesYinternacionales.respuesta_corta))
            ? safeParse(NacionalesYinternacionales.respuesta_corta)
            : [];

          const nacionalesValues = nacionalesYinternacionalesData.map(item =>
            item.total_nacional || item.nacionales || item.nacional || item.total_nacionales || item.valor || 0
          );
          const internacionalesValues = nacionalesYinternacionalesData.map(item =>
            item.total_internacional || item.internacionales || item.internacional || item.total_internacionales || item.valor || 0
          );

          const totalNacionales = nacionalesValues.reduce((acc, val) => acc + (val || 0), 0);
          const totalInternacionales = internacionalesValues.reduce((acc, val) => acc + (val || 0), 0);

          const years = nacionalesYinternacionalesData.map(item => item.curso || item.any_academico);
          setNationalInternationalData({
            years,
            nacionales: nacionalesValues,
            internacionales: internacionalesValues,
          });

          const graphIndex = 2;
          labelsArray[graphIndex] = ['Nacionales', 'Internacionales'];
          dataArray[graphIndex] = [totalNacionales, totalInternacionales];
          titlesArray[graphIndex] = "Comparación total de estudiantes nacionales e internacionales";
          tiposGraficaArray[graphIndex] = "Tarta";

          setChartLabels(labelsArray);
          setChartData(dataArray);
          setChartTitle(titlesArray);
          settipoGrafica(tiposGraficaArray);
        }

        if (chartType === "combined" || chartType === "single") {
          setChartLabels(labelsArray[0] || []);
          setChartData(dataArray[0] || []);
          chartTitle = parsedArray[0]?.titulo_grafico || chartTitle;
          setChartTitle(chartTitle);
          settipoGrafica(parsedArray[0]?.tipo_grafico || "none");

          if (!selectedCampus) {
            originalChartLabels.current = [labelsArray[0] || []];
            originalChartData.current = [dataArray[0] || []];
            originalChartTitle.current = chartTitle;
            setOriginalMaxY(Math.max(...(dataArray[0] || [0])));
          }
        } else if (chartType === "multiple") {
          setChartLabels(labelsArray);
          setChartData(dataArray);
          titlesArray = parsedArray.map(item => item.titulo_grafico || item.consulta_resumida || chartTitle);
          setChartTitle(titlesArray);
          settipoGrafica(parsedArray.map(item => item.tipo_grafico || "none"));
          setOriginalMaxY(Math.max(...(dataArray[0] || [0])));

          if (!selectedCampus) {
            originalChartLabels.current = labelsArray;
            originalChartData.current = dataArray;
            originalChartTitle.current = titlesArray;
          }
        }
      }
    } catch (error) {
      console.error('Error procesando respuesta_usuario:', error);
    }
  };

  const handleVideoEnd = () => {
    // Cambiar a tu streaming en vivo aquí
    setShowStreaming(true);
    // Iniciar tu streaming y asignar el stream al elemento de video
    // Por ejemplo: talkVideoRef.current.srcObject = tuStream;
  };

  const addNewMessage = async (
    response: AvatarResponse,
    senderType: string,
    senderName: string = ''
  ) => {
    if (messages.length === 0) {
      setHasFirstMessageSent(true);
      document.getElementById('parent-compontent')
        ?.classList.add('chat-darkened');
    }
    setMessages((prevMessages) => [
      ...prevMessages,
      { response, sender: senderType, author: senderName },
    ]);
    setDisplayText('Contestando...');
  };

  const setNewMessage = (
    prevMessage: AvatarResponse,
    newMessage: AvatarResponse
  ): AvatarResponse => {
    return {
      ...prevMessage,
      ...newMessage,
    };
  };

  const editMessageById = (id: string, newMessage: AvatarResponse | any) => {
    setMessages((prevMessages) =>
      prevMessages.map((message) =>
        message.response.id === id
          ? {
              ...message,
              response: setNewMessage(message.response, newMessage),
            }
          : message
      )
    );
  };

  // Valoración inline de la respuesta final de un asistente (mensajes con encuesta_satisfaccion).
  // Se guarda la nota en el propio mensaje para que el widget muestre el agradecimiento.
  const rateSatisfaction = async (messageId: string, puntuacion: number) => {
    if (!user || !userToken) {
      return false;
    }
    const ok = await sendSatisfactionRating(puntuacion, userToken, user, environment);
    if (ok) {
      editMessageById(messageId, { valoracion_enviada: puntuacion });
    }
    return ok;
  };

  const deleteMessageById = (id: string) => {
    const index = messages.findIndex((message) => message.response.id === id);
    messages.splice(index, 1);
    setMessages(structuredClone(messages));
  };

  useEffect(() => {
    switch (streamingState) {
      case ConnectionState.Connected:
        hasConnectedOnceRef.current = true;
        if (!userPausedRef.current && !isWaitingResponse && !isAvatarSpeakingRef.current) {
          void openMic();
        }
        break;
      case ConnectionState.Fail:
      case 'failed':
      case ConnectionState.Disconnected:
      case ConnectionState.Closed:
        // Cualquier pérdida del vídeo pasa a modo audio para que la
        // conversación no se corte; el vídeo se puede reintentar después.
        if (
          conversationStoppedForFeedbackRef.current ||
          audioFallbackModeRef.current ||
          !(streamingMode || streamingOnlyMode)
        ) {
          break;
        }
        activateAudioFallback({ reason: streamingState, autoStartMic: true });
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamingState]);

  useEffect(() => {
    (async () => {
      if (isChatSent && chatInput && isMicEnabled === undefined && uploadedFiles.length === 0) {
        await talk(chatInput);
      }
    })();
    if (!isChatSent) {
      isMicEnabled === undefined &&
        document.getElementById('chatInput')?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChatSent, chatInput, uploadedFiles.length]);

  async function handleSubmitChatInput() {
    if (interactionLimitReached) {
      showToast('Se ha alcanzado el límite de la sesión.', 'warn');
      return;
    }

    if (isBotThinking || loading) {
      showToast('Espera a que el bot termine de escribir.', 'warn');
      return;
    }

    if (uploadedFiles.length === 0) {
      if (chatInput.trim()) {
        setIsChatSent(true);
      }
      return;
    }

    const filesToUpload = [...uploadedFiles];
    const userMessage = chatInput.trim() || 'Archivos enviados';
    const uploadIdMessage = pendingUploadIdMessage;

    const userAvatarResponse: AvatarResponse = {
      id_proceso: '',
      respuesta_usuario: userMessage,
      respuesta_corta: {} as any,
      formato_respuesta_usuario: 'text' as any,
      archivo_upload: '',
      thread_id: uploadIdMessage,
      asignatura_id: '',
      url_img: '',
      file: new File([], ''),
      files: filesToUpload,
      id: '',
      idMessageLinked: uploadIdMessage,
      time: new Date().toISOString(),
      dominio: '',
    };

    setIsChatSent(false);
    setLoading(true);
    setIsBotThinking(true);
    addNewMessage(userAvatarResponse, 'user', '');
    editMessageById(uploadIdMessage, { archivo_upload: 'no' });
    setUploadedFiles([]);
    setPendingUploadIdMessage('');
    setChatInput('');
    setFileDescription('');
    showToast('Subiendo archivos...', 'info');

    try {
      const response = await uploadFilePartial(filesToUpload, userMessage);
      if (!response) {
        throw new Error('Error al subir archivos');
      }

      const responses = Array.isArray(response) ? response : [response];
      responses.forEach((avatarResponse) => {
        const gptResponse = toMessageFormat(avatarResponse);
        addNewMessage(gptResponse, 'bot', 'GPT-CEU');
      });
    } catch (err) {
      console.error('Error uploading files for message:', uploadIdMessage, err);
      showToast('Fallo al subir los archivos.', 'error');
    } finally {
      setLoading(false);
      setIsBotThinking(false);
    }
  }

  const onKeyDownChatInput = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await handleSubmitChatInput();
    }
  };

  const onChangeChatInput = (e: React.FormEvent<HTMLInputElement>) =>
    setChatInput(e.currentTarget.value);

  const onClickChatInput = (e: React.MouseEvent<HTMLInputElement>) =>
    isMicEnabled !== undefined && destroyMic(true);

  useEffect(() => {
    if (streamingMode || streamingOnlyMode) {
      const talkingBtn = document.getElementById('talking-btn') as HTMLElement;
      if (isMicEnabled === undefined) {
        if (talkingBtn) {
          talkingBtn.style.backgroundColor = 'rgba(0, 0, 0, 0.55)';
          talkingBtn.style.borderColor = 'rgba(255, 255, 255, 0.15)';
        }
        !isBotThinking && setDisplayText('Pulsa para conectar');
      } else if (isMicEnabled) {
        if (talkingBtn) {
          talkingBtn.style.backgroundColor = 'rgba(39, 174, 96, 0.65)';
          talkingBtn.style.borderColor = 'rgba(46, 204, 113, 0.4)';
        }
        !isBotThinking && setDisplayText('Escuchando...');
      } else {
        if (talkingBtn) {
          talkingBtn.style.backgroundColor = 'rgba(220, 53, 69, 0.6)';
          talkingBtn.style.borderColor = 'rgba(220, 53, 69, 0.3)';
        }
      }
    }
    if (voiceOnlyMode || voiceSpeechMode) {
      if (isMicEnabled === undefined) {
        // mic no creado
        setMicImg(micNormalImg);
      } else if (isMicEnabled) {
        // mic creado
        setMicImg(micListeningImg);
      } else {
        // mic pausado
        setMicImg(micForbiddenImg);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMicEnabled, isBotThinking]);

  const uploadFilePartial = (files: File[], description: string) =>
    uploadFiles(files, user?.email, description, environment);

  const context: AvatarContextModel = {
    addNewMessage,
    editMessageById,
    deleteMessageById,
    uploadFilePartial,
    talk,
    rateSatisfaction,
    userName,
  };

  const chatContext: ChatContextModel = {
    loading,
    setLoading,
    fileDescription,
    setFileDescription,
    uploadedFiles,
    setUploadedFiles,
    pendingUploadIdMessage,
    setPendingUploadIdMessage,
  };

  useEffect(() => {
    if (
      respuestaJson ||
      (examsOnly && examsOnly.length > 0) ||
      universityFunnelsData ||
      titulacionBarsData ||
      overviewBarsData ||
      rankingTableData ||
      personCardsData ||
      departmentOrgChartData
    ) {
      return;
    }
    
    if (chartLabels.length > 0 && chartData.length > 0) {
      setShowChart(true);
    } else {
      setShowChart(false);
    }
  }, [
    chartLabels,
    chartData,
    respuestaJson,
    examsOnly,
    universityFunnelsData,
    titulacionBarsData,
    overviewBarsData,
    rankingTableData,
    personCardsData,
    departmentOrgChartData,
  ]);


  const handleSetChatInput = (text: string) => setChatInput(text);

  const removeUploadedFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const isStreamingAvatar = streamingMode || streamingOnlyMode;

  const isPreparingAvatar =
    (!initialConfig && !errorOnInitialConfig) ||
    (!isAudioFallbackMode &&
      !isConversationStopped &&
      isStreamingAvatar &&
      ((shouldUseDidStreaming() &&
        streamingState !== ConnectionState.Connected) ||
        (isMicEnabled === undefined && !micStartedOnceRef.current)));

  const isConnectingVideo =
    Boolean(initialConfig) &&
    shouldUseDidStreaming() &&
    streamingState !== ConnectionState.Connected;

  const initializationMessage = !initialConfig
    ? 'Cargando tu avatar'
    : isConnectingVideo
      ? 'Conectando el vídeo del avatar'
      : micStartFailed
        ? 'No se pudo activar el micrófono'
        : 'Activando el micrófono';

  const initializationDescription = isConnectingVideo && showAudioFallbackOffer
    ? 'El vídeo está tardando más de lo normal. Puedes seguir la conversación solo con audio.'
    : !isConnectingVideo && micStartFailed
      ? 'Permite el acceso al micrófono en el navegador y vuelve a intentarlo.'
      : 'Estamos preparando la conexión. No cierres esta ventana.';

  const activateAudioFallback = ({
    reason,
    autoStartMic,
    fallbackSpeakTalk,
  }: {
    reason: string;
    autoStartMic: boolean;
    fallbackSpeakTalk?: AzureTalk;
  }) => {
    if (
      audioFallbackModeRef.current ||
      conversationStoppedForFeedbackRef.current ||
      !isStreamingAvatar
    ) {
      return;
    }

    console.warn('[Avatar] activando modo audio:', reason);
    const talkToRepeat = fallbackSpeakTalk ?? pendingSpeakTalkRef.current;
    const wasRetryingVideo = isRetryingVideoRef.current;
    isRetryingVideoRef.current = false;

    clearDidConnectionTimeout();
    clearDidSpeechWatch();
    suppressDidSpeechRef.current = false;
    if (dynamicDidFinishTimeoutRef.current) {
      clearTimeout(dynamicDidFinishTimeoutRef.current);
      dynamicDidFinishTimeoutRef.current = null;
    }
    audioFallbackModeRef.current = true;
    setIsAudioFallbackMode(true);
    setShowAudioFallbackOffer(false);
    setAvatarSpeaking(false);
    setStreamingState('audio_only');
    void disconnectAgent();
    const video = talkVideoRef.current;
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }

    // Si el vídeo se cae mientras la conversación está en pausa (p. ej. por
    // inactividad), al reanudar se intenta recuperar antes de seguir en audio.
    retryVideoOnResumeRef.current =
      userPausedRef.current &&
      !talkToRepeat &&
      (reason === ConnectionState.Disconnected || reason === ConnectionState.Closed);

    if (reason !== 'manual' && !retryVideoOnResumeRef.current) {
      showToast(
        wasRetryingVideo
          ? 'No se pudo recuperar el vídeo. Seguimos solo con audio.'
          : hasConnectedOnceRef.current
            ? 'Se ha perdido el vídeo. Seguimos solo con audio.'
            : 'No se pudo conectar el vídeo. Seguimos solo con audio.',
        'warn'
      );
    }

    if (talkToRepeat) {
      if (userPausedRef.current) {
        heldSpeakTalkRef.current = talkToRepeat;
      } else {
        void deliverSpeech(talkToRepeat);
      }
      return;
    }

    if (autoStartMic && !userPausedRef.current && !isWaitingResponse) {
      void openMic();
    }
  };

  const retryVideoConnection = async () => {
    if (!initialConfig) {
      window.location.reload();
      return;
    }
    if (isWaitingResponse || isAvatarSpeakingRef.current) {
      return;
    }

    retryVideoOnResumeRef.current = false;
    isRetryingVideoRef.current = true;
    clearSpeechBuffer();
    if (isMicOpened) {
      pauseMic();
    }
    stopAvatarSpeech();
    audioFallbackModeRef.current = false;
    setIsAudioFallbackMode(false);
    setStreamingState('');
    // Si falla, el efecto de streamingState vuelve a modo audio.
    const connected = await connect();
    if (connected) {
      isRetryingVideoRef.current = false;
    }
  };

  // Detiene lo que el avatar esté diciendo y cierra ese turno.
  const interruptAvatarSpeech = () => {
    if (shouldSpeakWithDid()) {
      if (!pendingSpeakTalkRef.current && !dynamicDidFinishTimeoutRef.current) {
        return;
      }
      clearDidSpeechWatch();
      if (dynamicDidFinishTimeoutRef.current) {
        clearTimeout(dynamicDidFinishTimeoutRef.current);
        dynamicDidFinishTimeoutRef.current = null;
      }
      const agent = agentManagerRef.current;
      if (agent && !shouldUseDynamicDidStreaming()) {
        // D-ID emitirá START/STOP de esta respuesta: se ignoran.
        suppressDidSpeechRef.current = true;
        try {
          if (agent.getIsInterruptAvailable?.()) {
            agent.interrupt({ type: 'click' });
          }
        } catch (error) {
          console.warn('[DIDAvatar] no se pudo interrumpir la respuesta:', error);
        }
      }
      if (!showDidIdleVideo('pause') && talkVideoRef.current) {
        talkVideoRef.current.muted = true;
      }
      setAvatarSpeaking(false);
      completeAvatarInteraction();
      return;
    }

    if (
      isAvatarSpeakingRef.current ||
      speechCompletionWatchdogRef.current ||
      speechTimeoutRef.current
    ) {
      stopAvatarSpeech();
      completeAvatarInteraction();
    }
  };

  const pauseConversation = () => {
    userPausedRef.current = true;
    setIsConversationPaused(true);
    clearSpeechBuffer();
    if (isMicOpened) {
      pauseMic();
    }
    interruptAvatarSpeech();
    setDisplayText('En pausa');
  };

  const resumeConversation = () => {
    userPausedRef.current = false;
    setIsConversationPaused(false);

    const heldTalk = heldSpeakTalkRef.current;
    heldSpeakTalkRef.current = null;
    if (heldTalk) {
      void deliverSpeech(heldTalk);
      return;
    }
    if (isWaitingResponse) {
      return;
    }
    if (audioFallbackModeRef.current && retryVideoOnResumeRef.current) {
      void retryVideoConnection();
      return;
    }
    setMicStartFailed(false);
    void openMic();
  };

  // Único control de la conversación: pausa (micro + voz del avatar) o reanuda.
  const toggleConversation = () => {
    if (interactionLimitReached || conversationStoppedForFeedbackRef.current) {
      return;
    }
    if (userPausedRef.current) {
      resumeConversation();
      return;
    }

    const isIdle =
      !isMicOpened &&
      !isWaitingResponse &&
      interactionLimitSessionRef.current?.canStart() !== false;
    if (isIdle) {
      setMicStartFailed(false);
      void openMic();
      return;
    }
    pauseConversation();
  };

  const handleConversationToggleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleConversation();
    }
  };

  const conversationPhase: ConversationPhase = isConversationPaused
    ? 'paused'
    : isAvatarSpeaking
      ? 'speaking'
      : isBotThinking
        ? 'thinking'
        : isMicEnabled === true
          ? 'listening'
          : 'idle';

  const avatarDisplayName = initialConfig?.avatarName || 'El avatar';

  const conversationStatusText = (() => {
    if (!isOnline) {
      return 'Sin conexión a internet';
    }
    switch (conversationPhase) {
      case 'paused':
        return 'En pausa · toca para continuar';
      case 'speaking':
        return `${avatarDisplayName} está hablando`;
      case 'thinking':
        return 'Pensando…';
      case 'listening':
        if (displayText === 'Te escucho, sigue si quieres...') {
          return 'Te escucho, sigue si quieres…';
        }
        return hasFirstMessageSent ? 'Te escucho' : 'Te escucho · habla para comenzar';
      default:
        if (micStartFailed) {
          return 'Micrófono no disponible · toca para reintentar';
        }
        return isMicEnabled === undefined ? 'Toca para hablar' : 'Un momento…';
    }
  })();

  const conversationToggleLabel =
    conversationPhase === 'paused'
      ? 'Reanudar la conversación'
      : conversationPhase === 'idle'
        ? 'Activar el micrófono'
        : 'Pausar la conversación';

  const InterviewStartIndicator = ({ split = false }: { split?: boolean }) => {
    if ((isAudioFallbackMode && isStreamingAvatar) || interactionLimitReached) {
      return null;
    }
    if (
      isMicEnabled === undefined &&
      !isConversationPaused &&
      !(isStreamingAvatar && micStartedOnceRef.current)
    ) {
      return null;
    }

    return (
      <div
        className={`interview-start-indicator${split ? ' interview-start-indicator--split' : ''} is-actionable${isConversationPaused ? ' is-paused' : ''}`}
        role="button"
        tabIndex={0}
        aria-pressed={isConversationPaused}
        aria-label={conversationToggleLabel}
        title={conversationToggleLabel}
        onClick={toggleConversation}
        onKeyDown={handleConversationToggleKeyDown}
      >
        <div className="interview-start-indicator__icon">
          {isConversationPaused ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z" />
              <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
            </svg>
          )}
        </div>
        <div className="interview-start-indicator__content">
          <p className="interview-start-indicator__text" aria-live="polite">
            {conversationStatusText}
          </p>
        </div>
      </div>
    );
  };

  const [dismissedSurveyMessageId, setDismissedSurveyMessageId] = useState<string | null>(null);
  const [pendingSurveyRating, setPendingSurveyRating] = useState<{
    messageId: string;
    score: number;
  } | null>(null);

  const lastChatMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const activeEndConversationResponse =
    lastChatMessage?.sender === 'bot' && isConversationEndedResponse(lastChatMessage.response)
      ? lastChatMessage.response
      : null;
  const activeEndConversationId = activeEndConversationResponse?.id;
  const activeEndConversationRating = activeEndConversationResponse?.valoracion_enviada;

  useEffect(() => {
    if (!activeEndConversationId || !activeEndConversationRating) {
      return;
    }

    const timer = window.setTimeout(() => {
      setDismissedSurveyMessageId(activeEndConversationId);
    }, 1800);

    return () => window.clearTimeout(timer);
  }, [activeEndConversationId, activeEndConversationRating]);

  const handlePopupSatisfactionRate = async (
    response: AvatarResponse,
    puntuacion: number
  ) => {
    const currentSelection =
      response.valoracion_enviada ??
      (pendingSurveyRating?.messageId === response.id
        ? pendingSurveyRating.score
        : null);

    if (currentSelection) {
      return;
    }

    setPendingSurveyRating({ messageId: response.id, score: puntuacion });
    const ok = await rateSatisfaction(response.id, puntuacion);
    if (!ok) {
      setPendingSurveyRating(null);
      showToast('No se pudo enviar la valoración.', 'warn');
    }
  };

  const renderSatisfactionPopup = () => {
    if (
      !activeEndConversationResponse ||
      dismissedSurveyMessageId === activeEndConversationResponse.id
    ) {
      return null;
    }

    const selectedScore =
      activeEndConversationResponse.valoracion_enviada ??
      (pendingSurveyRating?.messageId === activeEndConversationResponse.id
        ? pendingSurveyRating.score
        : null);
    const isRated = Boolean(selectedScore);

    return (
      <div
        className="satisfaction-popup"
        role="region"
        aria-label="Valoración de la conversación"
      >
        <span className="satisfaction-popup__text">
          {isRated
            ? '¡Gracias por tu valoración!'
            : '¿Te ha resultado útil la respuesta del asistente?'}
        </span>
        <div className="satisfaction-popup__actions">
          <button
            type="button"
            className={`satisfaction-popup__btn satisfaction-popup__btn--up${
              selectedScore === 5 ? ' is-selected' : ''
            }`}
            onClick={() =>
              handlePopupSatisfactionRate(activeEndConversationResponse, 5)
            }
            disabled={isRated}
            title="Buena respuesta"
            aria-label="Buena respuesta"
            aria-pressed={selectedScore === 5}
          >
            <ThumbUpIcon
              className="satisfaction-popup__icon"
              aria-hidden="true"
            />
            <span>Sí</span>
          </button>
          <button
            type="button"
            className={`satisfaction-popup__btn satisfaction-popup__btn--down${
              selectedScore === 1 ? ' is-selected' : ''
            }`}
            onClick={() =>
              handlePopupSatisfactionRate(activeEndConversationResponse, 1)
            }
            disabled={isRated}
            title="Mala respuesta"
            aria-label="Mala respuesta"
            aria-pressed={selectedScore === 1}
          >
            <ThumbDownIcon
              className="satisfaction-popup__icon"
              aria-hidden="true"
            />
            <span>No</span>
          </button>
          <button
            type="button"
            className="satisfaction-popup__close"
            onClick={() =>
              setDismissedSurveyMessageId(activeEndConversationResponse.id)
            }
            title="Cerrar"
            aria-label="Cerrar valoración"
          >
            ×
          </button>
        </div>
      </div>
    );
  };

  // Se renderizan como funciones (no como componentes) para que las
  // animaciones del círculo no se reinicien en cada render.
  const renderAudioOrb = (compact = false) => (
    <button
      type="button"
      className={`audio-orb is-${conversationPhase}${compact ? ' audio-orb--compact' : ''}`}
      onClick={toggleConversation}
      disabled={interactionLimitReached}
      aria-pressed={isConversationPaused}
      aria-label={conversationToggleLabel}
      title={conversationToggleLabel}
    >
      <span className="audio-orb__avatar" aria-hidden="true">
        <span className="audio-orb__initials">{avatarDisplayName.charAt(0)}</span>
        {initialConfig?.avatarImgUrl && (
          <img
            className="audio-orb__photo"
            src={initialConfig.avatarImgUrl}
            alt=""
            draggable={false}
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        )}
        {conversationPhase === 'paused' && (
          <span className="audio-orb__overlay">
            <svg className="audio-orb__play" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        )}
      </span>
      {conversationPhase !== 'paused' && (
        <span className="audio-orb__badge" aria-hidden="true">
          <span className="audio-orb__bars">
            <i />
            <i />
            <i />
            <i />
            <i />
          </span>
        </span>
      )}
    </button>
  );

  const renderVideoRetryNotice = () => (
    <div className="audio-fallback-notice">
      <span>Sin vídeo</span>
      <button
        type="button"
        onClick={() => void retryVideoConnection()}
        disabled={isBotThinking || isAvatarSpeaking || !isOnline}
        aria-label="Recuperar el vídeo de la videoconferencia"
        title="Recuperar vídeo"
      >
        Recuperar vídeo
      </button>
    </div>
  );

  const renderPendingUploadFiles = () => {
    if (uploadedFiles.length === 0) return null;

    return (
      <div className="pending-upload-files">
        {uploadedFiles.map((file, index) => (
          <div key={`${file.name}-${index}`} className="pending-upload-file">
            <span className="pending-upload-file__name">{file.name}</span>
            <button
              type="button"
              className="pending-upload-file__remove"
              onClick={() => removeUploadedFile(index)}
              aria-label={`Eliminar ${file.name}`}
            >
              x
            </button>
          </div>
        ))}
      </div>
    );
  };

  // Vista específica cuando solo hay exámenes en la respuesta JSON
  const ExamsOnlyDashboard: React.FC<{
    exams: NonNullable<DashboardData['examenes']>;
  }> = ({ exams }) => {
    return (
      <div className="exams-container exams-container-only">
        <h2 className="exams-title">Resultados de tus exámenes</h2>
        <div className="exams-grid">
          {exams.map((exam, index) => {
            const anyExam: any = exam || {};

            // Nota del alumno
            const rawNota =
              anyExam.nota_obtenida_alumno ??
              anyExam.nota ??
              anyExam.nota_examen ??
              anyExam.score;

            const isPending =
              (typeof anyExam.nota_visual === 'string' &&
                anyExam.nota_visual.toLowerCase() === 'pendiente') ||
              rawNota === null ||
              rawNota === undefined ||
              (typeof rawNota === 'string' && isNaN(parseFloat(rawNota)));

            const nota = isPending ? 0 : toNumberLocal(rawNota ?? 0);

            const maxA = toNumberLocal(
              anyExam.nota_max_alumno ??
                anyExam.nota_maxima ??
                anyExam.notaMaxima ??
                anyExam.max ??
                10
            );

            const media = toNumberLocal(
              anyExam.nota_obtenida_clase ??
                anyExam.media_clase ??
                anyExam.nota_media_clase ??
                anyExam.media ??
                0
            );

            const pct = maxA > 0 ? (nota / maxA) * 100 : 0;

            const color = isPending
              ? '#6b7280'
              : pct >= 70
              ? '#10b981'
              : pct >= 50
              ? '#f59e0b'
              : '#ef4444';

            const studentLabel = isPending
              ? 'Pendiente'
              : `${nota.toFixed(1)} / ${maxA.toFixed(1)}`;

            return (
              <div key={index} className="exam-card exam-card-only">
                <h3 className="exam-name">
                  {anyExam.nombre ?? anyExam.nombre_examen ?? 'Examen sin nombre'}
                </h3>
                <p className="exam-subject">
                  {anyExam.nombre_asignatura ?? 'Asignatura no especificada'}
                </p>

                {isPending ? (
                  <div className="pending-placeholder">
                    <p className="pending-text">Pendiente</p>
                    <p className="exam-media">Media de la clase: —</p>
                  </div>
                ) : (
                  <div className="bar-chart">
                    <div className="bar-group">
                      <span>Tú: {studentLabel}</span>
                      <div className="bar-container">
                        <div
                          className="bar student"
                          style={{
                            width: `${(nota / maxA) * 100}%`,
                            backgroundColor: color,
                          }}
                        />
                      </div>
                    </div>
                    <div className="bar-group">
                      <span>Clase: {media.toFixed(1)} / {maxA.toFixed(1)}</span>
                      <div className="bar-container">
                        <div
                          className="bar class"
                          style={{
                            width: `${(media / maxA) * 100}%`,
                            backgroundColor: '#6b7280',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <AvatarContext.Provider value={context}>
      <ChatContext.Provider value={chatContext}>
        <NoData errorOnInitialConfig={errorOnInitialConfig}>
        <div
          id="parent-compontent"
          className={avatarLayoutClassName}
        >
          {isPreparingAvatar && (
            <div className="avatar-startup-overlay" role="status" aria-live="polite">
              <div className="avatar-startup-card">
                {initialConfig?.avatarImgUrl && (
                  <img
                    className="avatar-startup-avatar"
                    src={initialConfig.avatarImgUrl}
                    alt=""
                    aria-hidden="true"
                  />
                )}
                <div className="avatar-startup-spinner" aria-hidden="true">
                  <span />
                </div>
                <p className="avatar-startup-eyebrow">CEU · AVATAR</p>
                <h1>{initializationMessage}</h1>
                <p className="avatar-startup-description">
                  {initializationDescription}
                </p>
                <div className="avatar-startup-progress" aria-hidden="true">
                  <span className={!initialConfig ? 'is-active' : 'is-complete'} />
                  <span
                    className={
                      !initialConfig ? '' : isConnectingVideo ? 'is-active' : 'is-complete'
                    }
                  />
                  <span
                    className={
                      isMicEnabled === true
                        ? 'is-complete'
                        : initialConfig && !isConnectingVideo
                          ? 'is-active'
                          : ''
                    }
                  />
                </div>
                {isConnectingVideo && showAudioFallbackOffer && (
                  <button
                    type="button"
                    className="avatar-startup-retry"
                    onClick={() =>
                      activateAudioFallback({ reason: 'manual', autoStartMic: true })
                    }
                  >
                    Continuar solo con audio
                  </button>
                )}
                {!isConnectingVideo && micStartFailed && (
                  <button
                    type="button"
                    className="avatar-startup-retry"
                    onClick={() => {
                      setMicStartFailed(false);
                      void openMic();
                    }}
                  >
                    Activar micrófono
                  </button>
                )}
              </div>
            </div>
          )}

          {avatarType?.toLowerCase() === 'gptceu' && (
            <>
              <div
                className={`chat-image-container ${hasFirstMessageSent ? 'chat-image-after-first-message' : ''
                  } ${environment === 'des' ? 'chat-image-des' : 'chat-image-prod'}`}
              ></div>

              <footer
                className={`avatar-footer ${hasFirstMessageSent ? 'footer-normal' : 'footer-initial'
                  }`}
              >
                <div className="footer-content">
                  <img alt="Footer Logo" className="footer-image" />
                  <span className="footer-separator">|</span>
                  <a
                    href="https://intranet.ceu.es/es-es/para-mi-trabajo/Paginas/IA-segura.aspx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ia-segura-link"
                  >
                    IA Segura
                  </a>
                </div>
              </footer>
            </>
          )}


          {showChart ? (
            <div className="split-screen-layout">
              <div className="chart-side">
                {(streamingMode || voiceOnlyMode || voiceSpeechMode) && (
                  <div className="split-avatar-widget">
                    <div
                      className={`split-avatar-preview${isAudioFallbackMode ? ' is-audio-only' : ''}`}
                    >
                      {initialConfig?.avatarImgUrl && !isAudioFallbackMode && (
                        <img
                          className="split-avatar-poster"
                          src={initialConfig.avatarImgUrl}
                          alt={initialConfig.avatarName || 'Avatar'}
                          aria-hidden={isDidStreamVisible}
                        />
                      )}
                      <video
                        ref={talkVideoRef}
                        className={`split-avatar-video ${
                          isDidStreamVisible ? 'is-visible' : ''
                        }`}
                        autoPlay
                        playsInline
                        muted={!isDidStreamVisible}
                        poster={initialConfig?.avatarImgUrl}
                        aria-label="Vídeo del avatar"
                      />
                      {isAudioFallbackMode && renderAudioOrb(true)}
                    </div>
                    <div className="split-avatar-info">
                      <span className="split-avatar-name">{avatarDisplayName}</span>
                      {isAudioFallbackMode && (
                        <>
                          {!interactionLimitReached && (
                            <span className="split-avatar-status" aria-live="polite">
                              {conversationStatusText}
                            </span>
                          )}
                          {renderVideoRetryNotice()}
                        </>
                      )}
                    </div>
                  </div>
                )}
                {examsOnly && examsOnly.length > 0 ? (
                  <ExamsOnlyDashboard exams={examsOnly} />
                ) : (
                  <>
                    {!isDashboard && (
                      <>
                        {departmentOrgChartData ? (
                          <div className="chart-container">
                            <DepartmentOrgChart data={departmentOrgChartData} />
                          </div>
                        ) : personCardsData ? (
                          <div className="chart-container">
                            <PersonCards data={personCardsData} />
                          </div>
                        ) : universityFunnelsData ? (
                          <div className="chart-container">
                            <UniversityFunnels data={universityFunnelsData} />
                          </div>
                        ) : titulacionBarsData ? (
                          <div className="chart-container">
                            <TitulacionBars data={titulacionBarsData} />
                          </div>
                        ) : overviewBarsData ? (
                          <div className="chart-container">
                            <OverviewBars data={overviewBarsData} />
                          </div>
                        ) : rankingTableData ? (
                          <div className="chart-container">
                            <RankingTable data={rankingTableData} />
                          </div>
                        ) : tipoGrafica === 'Tabla' ? (
                          <div className="table-container">
                            <h2>{chartTitle}</h2>
                            {tableData && (
                              <table>
                                <thead>
                                  <tr>
                                    {tableData.headers.map(header => (
                                      <th key={header}>{header}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {tableData.rows.map((row, index) => (
                                    <tr key={index}>
                                      {tableData.headers.map(header => (
                                        <td key={header}>
                                          {typeof row[header] === 'number' ? row[header].toFixed(2) : row[header]}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        ) : isTemplate === "Matriculados" ? (
                          <TemplateMatriculados
                            chartLabels={chartLabels as string[][]}
                            chartData={chartData as number[][]}
                            chartTitle={chartTitle}
                            campusEvolutions={campusEvolutions}
                            nuevoIngresoEvolutions={nuevoIngresoEvolutions}
                            reingresoEvolutions={reingresoEvolutions}
                            nationalInternationalData={nationalInternationalData}
                            originalMaxY={originalMaxY}
                          />
                        ) : isTemplate === "Tasas" ? (
                          <TemplateTasas
                            chartLabels={chartLabels as string[][]}
                            chartData={chartData as number[][]}
                            chartTitle={chartTitle as string[]}
                            tipoGrafica={tipoGrafica as unknown as string[]}
                            onSetChatInput={handleSetChatInput}
                          />
                        ) : Array.isArray(chartLabels[0]) ? (
                          <div className="chart-container multiple-charts">
                            {chartLabels.map((labels, idx) => (
                              <div className="chart-container" key={idx}>
                                {(() => {
                                  const tipo = Array.isArray(tipoGrafica)
                                    ? tipoGrafica[idx]
                                    : tipoGrafica;
                                  const titulo = Array.isArray(chartTitle)
                                    ? chartTitle[idx]
                                    : chartTitle;
                                  if (tipo === 'Tarta') {
                                    return (
                                      <PieChart
                                        labels={labels as string[]}
                                        data={chartData[idx] as number[]}
                                        title={titulo}
                                      />
                                    );
                                  } else if (tipo === 'GroupedBar') {
                                    return (
                                      <GroupedBarChart
                                        labels={labels as string[]}
                                        datasets={chartData[idx] as GroupedBarDataset[]}
                                        title={titulo}
                                      />
                                    );
                                  } else if (
                                    ["Barra", "Barras", "Barra.", "Barras."].includes(tipo)
                                  ) {
                                    return (
                                      <BarChart
                                        labels={labels as string[]}
                                        data={chartData[idx] as number[]}
                                        title={titulo}
                                      />
                                    );
                                  } else if (
                                    ["Linea", "Línea", "Linea.", "Línea."].includes(tipo)
                                  ) {
                                    return (
                                      <LineChart
                                        labels={labels as string[]}
                                        data={chartData[idx] as number[]}
                                        title={titulo}
                                      />
                                    );
                                  } else {
                                    return <p>Tipo de gráfico no soportado: {tipo}</p>;
                                  }
                                })()}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="chart-container">
                            {tipoGrafica === 'Tarta' ? (
                              <PieChart
                                labels={chartLabels as string[]}
                                data={chartData as number[]}
                                title={Array.isArray(chartTitle) ? chartTitle[0] : chartTitle}
                              />
                            ) : tipoGrafica === 'GroupedBar' ? (
                              <GroupedBarChart
                                labels={chartLabels as string[]}
                                datasets={chartData as GroupedBarDataset[]}
                                title={Array.isArray(chartTitle) ? chartTitle[0] : chartTitle}
                              />
                            ) : ["Barra", "Barras", "Barra.", "Barras."].includes(tipoGrafica) ? (
                              <BarChart
                                labels={chartLabels as string[]}
                                data={chartData as number[]}
                                title={Array.isArray(chartTitle) ? chartTitle[0] : chartTitle}
                              />
                            ) : ["Linea", "Línea", "Linea.", "Línea."].includes(tipoGrafica) ? (
                              <LineChart
                                labels={chartLabels as string[]}
                                data={chartData as number[]}
                                title={Array.isArray(chartTitle) ? chartTitle[0] : chartTitle}
                              />
                            ) : (
                              <p>Tipo de gráfico no soportado: {tipoGrafica}</p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {isDashboard && respuestaJson && (
                      <Dashboard data={respuestaJson} />
                    )}
                  </>
                )}
              </div>
              <div className="chat-side">
                <div className="chat-container column">
                  <Chat messages={messages} isBotThinking={isBotThinking} />
                  <div className={`chat-actions column ${hasFirstMessageSent ? 'chat-actions-after-first-message' : ''}`}>
                    {!streamingMode && (
                      <>
                        {interactionLimitReached && (
                          <div className="interaction-limit-notice" role="alert">
                            Se ha alcanzado el límite de interacciones de esta sesión.
                          </div>
                        )}
                        {renderSatisfactionPopup()}
                        {renderPendingUploadFiles()}
                        <div className="chat-input-container">
                          <input
                            type="text"
                            id="chatInput"
                            name="chatInput"
                            className="chat-input"
                            value={chatInput}
                            onChange={onChangeChatInput}
                            onKeyDown={onKeyDownChatInput}
                            onClick={onClickChatInput}
                            disabled={interactionLimitReached}
                            autoComplete="off"
                            placeholder="Mensaje para tu asistente"
                          />
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="32"
                            height="32"
                            viewBox="0 0 24 24"
                            className={`chat-icon ${(!chatInput.trim() && uploadedFiles.length === 0) || isBotThinking || loading || interactionLimitReached ? 'disabled' : ''}`}
                            onClick={handleSubmitChatInput}
                          >
                            <path fill="currentColor" d="M3 20v-6l8-2l-8-2V4l19 8z" />
                          </svg>
                        </div>
                        {showSuggestions && (
                          <div className="suggestions-container" ref={suggestionsRef}>
                            {suggestions.map((frase, index) => (
                              <div
                                key={index}
                                className="suggestion-item"
                                onClick={() => {
                                  setChatInput(frase);
                                  setShowSuggestions(false);
                                }}
                              >
                                {frase}
                              </div>
                            ))}
                          </div>
                        )}
                        {!hasFirstMessageSent && (
                          <div className="tag-container">
                            {tags.map((tag, index) => (
                              <button
                                key={index}
                                className="chat-tag"
                                onClick={() => {
                                  const frasesDelTag = tagFrases[tag] || [];
                                  const fraseAleatoria = getRandomPhrase(frasesDelTag);
                                  setChatInput(fraseAleatoria);
                                  if (frasesDelTag.length > 1) {
                                    setSuggestions(frasesDelTag.filter(f => f !== fraseAleatoria));
                                    setShowSuggestions(true);
                                  } else {
                                    setShowSuggestions(false);
                                  }
                                }}
                              >
                                {tag}
                              </button>
                            ))}
                          </div>
                        )}
                        <ConversationDownloadButton />
                        <FeedbackButton />
                      </>
                    )}
                    {(streamingMode || voiceOnlyMode || voiceSpeechMode) && (
                      <>
                        {interactionLimitReached && isStreamingAvatar && (
                          <div className="interaction-limit-notice" role="alert">
                            Se ha alcanzado el límite de interacciones de esta sesión.
                          </div>
                        )}
                        <InterviewStartIndicator split />
                        {isMicEnabled === undefined && !isStreamingAvatar && !isConversationPaused && (
                          <button
                            className="chat-mic-btn"
                            onClick={() => sttFromMic(true)}
                            disabled={interactionLimitReached}
                            aria-label="Hablar con el avatar"
                          >
                            <img src={micImg} alt="" aria-hidden="true" />
                            <span>Hablar</span>
                          </button>
                        )}
                        <ConversationDownloadButton />
                        <FeedbackButton />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {(streamingMode || streamingOnlyMode) && (
                <div className={`subarea ${streamingOnlyMode ? 'subarea-img-full' : 'subarea-img'} ${showSuggestions ? 'move-up' : ''}`}>
                  <div className="avatar-container">
                    {showStreaming ? (
                      <>
                        {initialConfig?.avatarImgUrl && !isAudioFallbackMode && (
                          <img
                            className="streaming-avatar-poster"
                            src={initialConfig.avatarImgUrl}
                            alt={initialConfig.avatarName || 'Avatar'}
                            aria-hidden={isDidStreamVisible}
                          />
                        )}
                        <video
                          ref={talkVideoRef}
                          className={`streaming-avatar-video ${isDidStreamVisible ? 'is-visible' : ''}`}
                          autoPlay
                          playsInline
                          preload="auto"
                          poster={initialConfig?.avatarImgUrl}
                        />
                        {isAudioFallbackMode && (
                          <div className="audio-fallback-stage">
                            {initialConfig?.avatarImgUrl && (
                              <img
                                className="audio-fallback-backdrop"
                                src={initialConfig.avatarImgUrl}
                                alt=""
                                aria-hidden="true"
                              />
                            )}
                            {renderVideoRetryNotice()}
                            {renderAudioOrb()}
                            <div className="audio-orb__caption">
                              <p className="audio-orb__name">{avatarDisplayName}</p>
                              {!interactionLimitReached && (
                                <p className="audio-orb__status" aria-live="polite">
                                  {conversationPhase === 'speaking' && isOnline
                                    ? 'Hablando…'
                                    : conversationStatusText}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="streaming-avatar-controls">
                          {interactionLimitReached && (
                            <div className="interaction-limit-notice" role="alert">
                              Se ha alcanzado el límite de interacciones de esta sesión.
                            </div>
                          )}
                          <InterviewStartIndicator />
                          <ConversationDownloadButton />
                          <FeedbackButton />
                        </div>
                      </>
                    ) : (
                      <ReactPlayer
                        url={initialConfig?.videoInicial}
                        playing={true}
                        controls={true}
                        width="100%"
                        height="100vh"
                        onEnded={handleVideoEnd}
                        config={{
                          vimeo: {
                            playerOptions:
                            {
                              byline: false,
                              portrait: false,
                              title: false,
                              color: 'ffffff',
                            },
                          },
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
              {initialConfig && !streamingOnlyMode && (
                <div className={`subarea ${!streamingMode ? 'subarea-only-text' : 'subarea-text'} ${showSuggestions ? 'move-down' : ''}`}>
                  <div className="chat-container column">
                    <Chat messages={messages} isBotThinking={isBotThinking} />
                    <div className={`chat-actions column ${hasFirstMessageSent ? 'chat-actions-after-first-message' : ''}`}>
                      {!streamingMode && (
                        <>
                          {interactionLimitReached && (
                            <div className="interaction-limit-notice" role="alert">
                              Se ha alcanzado el límite de interacciones de esta sesión.
                            </div>
                          )}
                          {renderSatisfactionPopup()}
                          {renderPendingUploadFiles()}
                          <div className="chat-input-container">
                            <input
                              type="text"
                              id="chatInput"
                              name="chatInput"
                              className="chat-input"
                              value={chatInput}
                              onChange={onChangeChatInput}
                              onKeyDown={onKeyDownChatInput}
                              onClick={onClickChatInput}
                              disabled={interactionLimitReached}
                              autoComplete="off"
                              placeholder="Mensaje para tu asistente"
                            />
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 24 24"
                              className={`chat-icon ${(!chatInput.trim() && uploadedFiles.length === 0) || isBotThinking || loading || interactionLimitReached ? 'disabled' : ''}`}
                              onClick={handleSubmitChatInput}
                            >
                              <path fill="currentColor" d="M3 20v-6l8-2l-8-2V4l19 8z" />
                            </svg>
                          </div>
                          {showSuggestions && (
                            <div className="suggestions-container" ref={suggestionsRef}>
                              {suggestions.map((frase, index) => (
                                <div
                                  key={index}
                                  className="suggestion-item"
                                  onClick={() => {
                                    setChatInput(frase);
                                    setShowSuggestions(false);
                                  }}
                                >
                                  {frase}
                                </div>
                              ))}
                            </div>
                          )}
                          {!hasFirstMessageSent && (
                            <div className="tag-container">
                              {tags.map((tag, index) => (
                                <button
                                  key={index}
                                  className="chat-tag"
                                  onClick={() => {
                                    const frasesDelTag = tagFrases[tag] || [];
                                    const fraseAleatoria = getRandomPhrase(frasesDelTag);
                                    setChatInput(fraseAleatoria);
                                    if (frasesDelTag.length > 1) {
                                      setSuggestions(frasesDelTag.filter(f => f !== fraseAleatoria));
                                      setShowSuggestions(true);
                                    } else {
                                      setShowSuggestions(false);
                                    }
                                  }}
                                >
                                  {tag}
                                </button>
                              ))}
                            </div>
                          )}
                          <ConversationDownloadButton />
                          <FeedbackButton />
                        </>
                      )}
                      {(voiceOnlyMode || voiceSpeechMode) && (
                        <>
                          <InterviewStartIndicator split />
                          {isMicEnabled === undefined && !isConversationPaused && (
                            <button
                              className="chat-mic-btn"
                              onClick={() => sttFromMic(true)}
                              disabled={interactionLimitReached}
                              aria-label="Hablar con el avatar"
                            >
                              <img src={micImg} alt="" aria-hidden="true" />
                              <span>Hablar</span>
                            </button>
                          )}
                          <ConversationDownloadButton />
                          <FeedbackButton />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <LogoutBtn
            userToken={userToken}
            logout={logout}
            initials={userInitials}
            hasFirstMessageSent={hasFirstMessageSent}
            userName={userName}
            userRole={role}
            canAccessDes={canAccessDes}
            environment={environment}
            onEnvironmentChange={(env) => {
              updateEnvironment(env);
              window.location.reload();
            }}
          />
          <FeedbackEmailModal />
          <FeedbackSuccessOverlay />
        </div>
        </NoData>
      </ChatContext.Provider>
    </AvatarContext.Provider>
  );
}
export default AvatarApp;